import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Shield,
  MagnifyingGlass,
  Sparkle,
  Trash,
  ArrowRight,
  WarningCircle,
  CheckCircle,
  CircleNotch,
  ArrowsClockwise,
  Printer,
} from "@phosphor-icons/react";
import { TimelineNode } from "@/components/StepTimeline";
import { StatusText } from "@/components/StatusText";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatQty = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

// Oct 13 2026, user's explicit ask ("eliminate this pill design... make
// sure it is ERP-level professional... show the number in the text
// directly instead of in a pill, the pill is irrelevant... for GST push,
// if recorded, just a nice 'GST recorded' or a tick would work better";
// then: "Created in SAP/GI Posted/Sync Failed/Sync Pending should also
// be like GST Recorded with a tick and sentence case rather than all
// caps") - every order-level status (incl. ship-line status) renders as
// a plain icon + Title Case text via the shared <StatusText>, no
// box/pill at all. Plain reference data (an outbound delivery number)
// renders as plain text with no icon.

// Sep 21 2026, user's explicit ask - reverted the earlier "-MOV" display
// rename below; show the real "{SITE}-HOLD" warehouse name as-is again.
const movDisplayName = (id) => id;

// Popover for the pre-STO backend relocation (RAW/actual warehouse ->
// P8-HOLD) that happens silently while creating an outbound STO from
// Site P8 - user's explicit ask (Sep 2026): "same as you add in STO
// receipt" - shows each moved item next to its own real SAP Goods
// Movement ID.
const RelocationPopover = ({ lines, to, stoId, testIdPrefix = "stock-transfer-recent-relocation", children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="underline-offset-2 hover:underline text-left" onClick={(e) => e.stopPropagation()} data-testid={`${testIdPrefix}-trigger-${stoId}`}>
        {children}
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-72 p-3" align="start" data-testid={`${testIdPrefix}-popover-${stoId}`}>
      <p className="text-xs font-semibold text-[#101828] mb-2">Warehouse Move &rarr; {to}</p>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-[#667085]">
            <th className="text-left font-medium py-1">Item</th>
            <th className="text-right font-medium py-1">Movement</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.product_id} className="border-t border-[#EAECF0]">
              <td className="py-1.5 pr-2 font-mono text-[#344054]">{l.product_id}</td>
              <td className="py-1.5 text-right font-mono text-[#027A48]">GM {l.gac_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PopoverContent>
  </Popover>
);
const formatSapId = (id) => (id ? id.replace(/^0+(?=\d)/, "") : id);
const cleanSapMessage = (msg) => (msg ? msg.replace(/\s{2,}/g, " ").trim() : msg);
const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : null);

// Sep 22 2026 - per-order audit trail for the instant multi-line combine
// automation (see stock_transfer_service._run_instant_combine) - user's
// explicit ask, so staff can see exactly which step it's on/stuck at.
const COMBINE_STEP_LABEL = {
  checking: "checking SAP...",
  creating_request: "creating combined request in SAP...",
  attaching_lines: "attaching lines to the combined request...",
  releasing: "releasing the combined request...",
  finding_task: "looking up the resulting Pick Task...",
  confirming_task: "confirming the combined Pick Task...",
  confirmed: "combined Pick Task confirmed, waiting for SAP to finish it",
  error: "retrying automatically",
  mismatch: "line mismatch detected, paused for review",
  stuck: "gave up after 5 attempts - needs manual review in SAP",
};

// Per-line Ship Status (Aug 2026, user's explicit ask - header badge on
// the list view intentionally stays one combined status, this only backs
// the detail modal's "click to see detail" items table).
const SHIP_STATUS_STYLE = {
  shipped: { label: "Shipped", tone: "success" },
  failed: { label: "Failed", tone: "danger" },
  insufficient_stock: { label: "Insufficient Stock", tone: "warning" },
  pending: { label: "Pending", tone: "neutral" },
};
const lineShipStatus = (order, item) => {
  const list = order.gi_line_status || [];
  const found = list.find((l) => l.line_no != null && l.line_no === item.line_no) || list.find((l) => l.product_id === item.product_id);
  if (found) return found;
  // Older orders posted before gi_line_status existed - fall back to
  // the order's own combined status rather than showing nothing.
  if (order.gi_status === "posted") return { status: "shipped", note: null };
  if (order.gi_status === "failed") return { status: "failed", note: null };
  if (order.gi_status === "insufficient_stock") return { status: "insufficient_stock", note: null };
  return { status: "pending", note: null };
};
const parseGiError = (raw) => {
  if (!raw) return null;
  const jsonMatch = raw.match(/\{.*\}/s);
  if (jsonMatch) {
    try {
      const value = JSON.parse(jsonMatch[0])?.error?.message?.value;
      if (value) return value.split("::").filter(Boolean).join(" — ");
    } catch { /* fall through to raw cleanup below */ }
  }
  return cleanSapMessage(raw);
};
const todayISO = () => new Date().toISOString().slice(0, 10);

// Real (not simulated) steps this app's live SAP write actually goes
// through today - drives the progress bar in the confirm dialog. The
// last step (Goods Issue) is genuinely async - SAP's own scheduling
// decides when the Customer Requirement becomes an Outbound Delivery
// Request, and our own retry loop live-checks real stock before every
// attempt (Aug 27 2026) - the dialog keeps polling it after "Create in
// SAP" finishes so the user can watch it happen, but can also close the
// dialog any time; the background job keeps running regardless.
const STO_STEPS = [
  { key: "validate", label: "Validate order" },
  { key: "check", label: "SAP availability & data check" },
  { key: "create", label: "Create in SAP" },
  { key: "post_goods_issue", label: "Post Goods Issue (SAP delivery)" },
  { key: "erp_sync", label: "Sync to ERP Portal" },
];

// Sep 22 2026, 4-button STO redesign (STO_4BUTTON_REDESIGN.md) - human
// labels for the per-category "Retry" buttons on Button 1's validation
// issues (only stock/valuation/activation are re-runnable individually
// via /stock-transfer/validate's `only_checks`; "staging" retries the
// whole /stock-transfer/stage call since the goods movement itself has
// no sub-category).
const CHECK_LABELS = { stock: "Stock", valuation: "Valuation", activation: "Activation", staging: "Staging" };

// One selected line item. Every field the SAP ByDesign reference screen
// shows per-line (Source Warehouse, Available Qty, Ship-from Site,
// Availability Status) lives here.
const emptyLine = (product) => ({
  key: `${product.product_id}-${Date.now()}`,
  product_id: product.product_id,
  description: product.description,
  unit_of_measure: product.unit_of_measure,
  hsn_code: product.hsn_code,
  locations: product.locations || [],
  is_consumable: product.is_consumable || false,
  source_warehouse_id: "",
  ship_from_site_id: "",
  available_qty: null,
  requested_qty: "",
  suggestion: null,
  error: null,
});
// Shared body for BOTH the create-flow confirm dialog (once the order
// exists in SAP) and the Recent Orders detail modal (user's explicit
// ask, Aug 2026: "the dialog that opens when I create the sto should be
// same as the detail dialog") - one single source of truth for what an
// order's live status looks like, so the two views can never drift
// apart again.
const DebugScreenshotsViewer = ({ stoId }) => {
  const [open, setOpen] = useState(false);
  const [shots, setShots] = useState(null);
  const toggle = async () => {
    if (!open && !shots) {
      try {
        const { data } = await axios.get(`${API}/stock-transfer/orders/${stoId}/debug-screenshots`);
        setShots(data);
      } catch {
        setShots([]);
      }
    }
    setOpen((o) => !o);
  };
  return (
    <div className="mt-2">
      <button type="button" onClick={toggle} className="text-xs text-[#175CD3] underline" data-testid="stock-transfer-debug-screenshots-toggle">
        {open ? "Hide" : "View"} debug screenshots (admin)
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-3 gap-2" data-testid="stock-transfer-debug-screenshots-grid">
          {shots === null ? <p className="text-xs text-[#98A2B3]">Loading...</p>
            : shots.length === 0 ? <p className="text-xs text-[#98A2B3]">No debug screenshots yet for this order.</p>
            : shots.map((s) => (
              <a key={s.filename} href={`${API}/stock-transfer/debug-screenshots/${s.filename}`} target="_blank" rel="noreferrer" className="block border border-[#EAECF0] rounded-sm overflow-hidden hover:opacity-80">
                <img src={`${API}/stock-transfer/debug-screenshots/${s.filename}`} alt={s.step} className="w-full h-20 object-cover" />
                <p className="text-[10px] px-1 py-0.5 bg-[#F9FAFB] truncate">{s.step}</p>
              </a>
            ))}
        </div>
      )}
    </div>
  );
};

// Sep 23 2026, user's explicit design ask ("no rotating wheels in four
// colors... doesn't look like a button, looks like an update panel") -
// vertical status timeline (STEP_ICON/STEP_PILL/TimelineNode) now lives
// in /app/components/StepTimeline.jsx (Oct 2026 extraction, so the GRN
// page's "Post to SAP" modal can share the exact same look).

export const OrderDetailBody = ({ order, retryingStoId, onRetryOrder, retryingErpStoId, onRetryErpSync, onManualErpLink, verifyingErpStoId, onVerifyErpSync, retryingPostingStoId, onRetryPosting, completingGiStoId, onCompleteManualGi, pausingAutoRetryStoId, onPauseAutoRetry, stoppingGiStoId, onForceStopGi, isAdmin, notifications, activateResults, activatingId, confirmActivateFor, setConfirmActivateFor, onActivate }) => {
  if (!order) return null;
  // User's explicit ask (Sep 3 2026): the "Activate this site" fix action
  // for a "No valid planning data..." rejection used to live ONLY in the
  // separate "Action Needed" banner above the orders list - easy to miss
  // when looking at this order's own detail modal instead. Surface the
  // exact same action here too, for this specific order, when it applies.
  const matchingNotification = (notifications || []).find((n) => n.sto_id === order.sto_id);
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-[#667085]">Created {new Date(order.created_at).toLocaleString("en-IN")} by {order.created_by}</p>
        {order.erp_portal_status === "synced" && (
          <Button
            size="sm" variant="outline" className="text-xs h-7 shrink-0"
            onClick={() => window.open(`/inventory/inter-plant-transfer/${order.sto_id}/delivery-note`, "_blank")}
            data-testid="stock-transfer-print-delivery-note-btn"
          >
            <Printer size={13} className="mr-1" /> Print Delivery Note
          </Button>
        )}
      </div>

      {order.error_message ? (
        <div className="bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-3 text-sm text-[#912018] flex items-start gap-2" data-testid="stock-transfer-detail-error">
          <WarningCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-bold">SAP rejected this order:</p>
            <p className="mt-0.5">{cleanSapMessage(order.error_message)}</p>
            <Button
              size="sm" variant="outline" className="mt-2"
              onClick={() => onRetryOrder(order.sto_id)}
              disabled={retryingStoId === order.sto_id}
              data-testid="stock-transfer-detail-retry-button"
            >
              {retryingStoId === order.sto_id ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}
              Retry this order
            </Button>

            {matchingNotification && (
              <div className="mt-3 bg-[#FFFAEB] border border-[#FEC84B] rounded-sm p-2 space-y-2" data-testid="stock-transfer-detail-activate-panel">
                <p className="text-[#93370D]">
                  Product <span className="font-bold">{matchingNotification.product_id}</span>{" "}
                  {matchingNotification.type === "missing_valuation"
                    ? <>has no Cost/Valuation set up at site <span className="font-bold">{matchingNotification.site_id}</span></>
                    : <>is not activated (Planning/Logistics) at site <span className="font-bold">{matchingNotification.site_id}</span></>}
                  {" "}- this is why SAP rejected it.
                </p>
                {(() => {
                  const result = activateResults?.[matchingNotification._id];
                  const isValuationNotif = matchingNotification.type === "missing_valuation";
                  const resolved = isValuationNotif ? result?.valuation === "ok" : result?.planning_logistics === "ok";
                  if (result) {
                    return (
                      <div className="space-y-1" data-testid={`stock-transfer-detail-activate-result-${matchingNotification._id}`}>
                        <p className={result.planning_logistics === "ok" ? "text-[#027A48] font-bold" : "text-[#B42318] font-bold"}>
                          Planning / Availability / Logistics: {result.planning_logistics === "ok" ? "Activated successfully." : cleanSapMessage(result.planning_logistics)}
                        </p>
                        {result.valuation && (
                          <p className={result.valuation === "ok" ? "text-[#027A48]" : "text-[#B42318]"}>
                            Valuation: {result.valuation === "ok" ? "Activated successfully." : cleanSapMessage(result.valuation)}
                          </p>
                        )}
                        {resolved && (
                          <Button size="sm" variant="outline" onClick={() => onRetryOrder(matchingNotification.sto_id)} disabled={retryingStoId === matchingNotification.sto_id} data-testid={`stock-transfer-detail-activate-retry-${matchingNotification.sto_id}`}>
                            {retryingStoId === matchingNotification.sto_id ? <CircleNotch size={14} className="animate-spin" /> : `Retry ${matchingNotification.sto_id}`}
                          </Button>
                        )}
                      </div>
                    );
                  }
                  if (confirmActivateFor === matchingNotification._id) {
                    return (
                      <div className="flex items-center gap-2 bg-white border border-[#FEC84B] rounded-sm p-2">
                        <span className="text-[#93370D]">This writes directly to live SAP master data - are you sure?</span>
                        <Button size="sm" onClick={() => onActivate(matchingNotification)} disabled={activatingId === matchingNotification._id} data-testid={`stock-transfer-detail-activate-confirm-${matchingNotification._id}`}>
                          {activatingId === matchingNotification._id ? <CircleNotch size={14} className="animate-spin" /> : "Yes, activate"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmActivateFor(null)} data-testid={`stock-transfer-detail-activate-cancel-${matchingNotification._id}`}>Cancel</Button>
                      </div>
                    );
                  }
                  return (
                    <Button size="sm" variant="outline" onClick={() => setConfirmActivateFor(matchingNotification._id)} data-testid={`stock-transfer-detail-activate-button-${matchingNotification._id}`}>
                      Activate {matchingNotification.site_id} for {matchingNotification.product_id}
                    </Button>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border border-[#EAECF0] rounded-sm p-4" data-testid="stock-transfer-detail-timeline">
          <TimelineNode testId="stock-transfer-detail-success" label="Order Created in SAP" state={order.status === "created_in_sap" ? "done" : "active"}>
            {order.status === "created_in_sap" ? (
              <p className="text-xs text-[#667085] mt-0.5">SAP Order #{formatSapId(order.sap_order_id) || "—"}</p>
            ) : (
              <p className="text-xs text-[#667085] mt-0.5" data-testid="stock-transfer-detail-no-error">Submitting to SAP now...</p>
            )}
          </TimelineNode>

          {(() => {
            // Sep 23 2026, user's explicit ask - merged what used to be
            // two separate timeline nodes ("Delivery Combined" + "Goods
            // Issue Posted") into ONE, since to the user it's a single
            // automated pipeline (attach -> release -> confirm task ->
            // post GI) with a single outcome: is Goods Issue posted or
            // not. Two nodes with two separate "Retry" buttons for the
            // same underlying thing was the exact confusion reported.
            const stuck = order.combine_attempt?.stuck;
            const giState = order.gi_status === "posted" ? "done"
              : order.gi_status === "awaiting_manual_gi" ? "manual"
              : (stuck || ["failed", "not_found_timeout", "insufficient_stock"].includes(order.gi_status)) ? "failed"
              : order.status === "created_in_sap" ? "active" : "pending";
            const canRetryPosting = stuck || ["failed", "not_found_timeout"].includes(order.gi_status);
            const canSwitchToManual = order.gi_status !== "awaiting_manual_gi" && (order.gi_job_running || order.gi_status === "insufficient_stock" || canRetryPosting);
            // Sep 23 2026, user's explicit ask - show BOTH identifiers
            // as they become known, not a fallback chain that hides one:
            // the numeric Delivery Request # (SAP creates this first,
            // e.g. "61958") and the final alphanumeric Outbound Delivery
            // # (only exists once released, e.g. "P9D1-4280").
            const whatsStuck = stuck ? order.combine_attempt.detail
              : (order.gi_status === "failed" || order.gi_status === "not_found_timeout") ? (parseGiError(order.gi_error) || "Could not post Goods Issue automatically.")
              : order.gi_status === "insufficient_stock" ? "Insufficient live stock at the source warehouse."
              : null;
            return (
              <TimelineNode testId="stock-transfer-detail-gi-status" label="Goods Issue Posted" state={giState}>
                {order.gi_delivery_request_id && (
                  <p className="text-xs text-[#667085] mt-0.5" data-testid="stock-transfer-detail-delivery-request-id">Delivery Request #: {order.gi_delivery_request_id}</p>
                )}
                {order.outbound_delivery_ids?.length > 0 ? (
                  <p className="text-xs text-[#667085] mt-0.5" data-testid="stock-transfer-detail-delivery-ids">Outbound Delivery #: {order.outbound_delivery_ids.join(", ")}</p>
                ) : order.outbound_delivery_object_id && (
                  <p className="text-xs text-[#667085] mt-0.5" data-testid="stock-transfer-detail-legacy-object-id">Outbound Delivery Request #: {order.outbound_delivery_object_id}</p>
                )}
                {order.gi_status === "posted" && formatDateTime(order.gi_posted_at) && (
                  <p className="text-xs text-[#667085] mt-0.5" data-testid="stock-transfer-detail-gi-posted-at">Posted at: {formatDateTime(order.gi_posted_at)}</p>
                )}
                {(() => {
                  const relocatedLines = (order.items || []).filter((it) => it.p8_relocation).map((it) => ({ product_id: it.product_id, gac_id: it.p8_relocation.gac_id }));
                  const relocationTo = movDisplayName((order.items || []).find((it) => it.p8_relocation)?.p8_relocation?.to);
                  return relocatedLines.length > 0 && (
                    <p className="text-xs text-[#667085] mt-0.5" data-testid="stock-transfer-detail-relocation">
                      {relocatedLines.length} item{relocatedLines.length === 1 ? "" : "s"} moved into {relocationTo}.{" "}
                      <RelocationPopover lines={relocatedLines} to={relocationTo} stoId={order.sto_id} testIdPrefix="stock-transfer-detail-relocation">
                        <span className="underline cursor-pointer">View details</span>
                      </RelocationPopover>
                    </p>
                  );
                })()}
                {giState === "active" && (
                  <p className="text-xs text-[#98A2B3] mt-1" data-testid="stock-transfer-detail-gi-progress">Auto-checking (instantly on a live SAP update, every 20s otherwise)...</p>
                )}
                {/* Sep 23 2026, user's explicit ask - clearly say WHAT got
                    stuck, in plain English, before showing any button. */}
                {whatsStuck && (
                  <p className="text-xs text-[#B42318] mt-0.5" data-testid="stock-transfer-detail-whats-stuck">{whatsStuck}</p>
                )}
                {order.gi_status === "awaiting_manual_gi" && (
                  <>
                    {order.gi_error && (
                      <p className="text-xs text-[#B54708] mt-0.5" data-testid="stock-transfer-detail-whats-stuck-manual">{parseGiError(order.gi_error)}</p>
                    )}
                    <p className="mt-1 text-xs text-[#667085]" data-testid="stock-transfer-detail-manual-gi-instructions">
                      In SAP, find this order{order.outbound_delivery_ids?.length > 0 ? ` (Delivery ${order.outbound_delivery_ids.join(", ")})` : order.gi_delivery_request_id ? ` (Delivery Request ${order.gi_delivery_request_id})` : ""}, fill Vehicle No./Transportation Mode/Place Of Supply/G.R No./Date Of Supply/Freight Forwarder, then post Goods Issue.
                      {order.ship_to_site_id === "P8" ? ` Then complete the Inbound STO Receipt at Site P8 (it will land in ${movDisplayName("P8-HOLD")}).` : ""}
                    </p>
                    {order.manual_hold_until && new Date(order.manual_hold_until) > new Date() && (
                      <p className="mt-1 text-xs text-[#0E7C4A]" data-testid="stock-transfer-detail-auto-retry-paused">
                        Auto-retry paused until {new Date(order.manual_hold_until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - safe to work this order directly in SAP now.
                      </p>
                    )}
                  </>
                )}
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {canRetryPosting && (
                    <Button size="sm" onClick={() => onRetryPosting(order.sto_id)} disabled={retryingPostingStoId === order.sto_id} data-testid="stock-transfer-detail-retry-posting-button">
                      {retryingPostingStoId === order.sto_id ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}
                      Retry Posting
                    </Button>
                  )}
                  {canSwitchToManual && (
                    <Button size="sm" variant="outline" onClick={() => onForceStopGi(order.sto_id)} disabled={stoppingGiStoId === order.sto_id} data-testid="stock-transfer-detail-force-stop-gi-button">
                      {stoppingGiStoId === order.sto_id ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}
                      Switch to Manual in SAP
                    </Button>
                  )}
                  {order.gi_status === "awaiting_manual_gi" && (
                    <>
                      <Button
                        size="sm" variant="outline" onClick={() => onPauseAutoRetry(order.sto_id)} disabled={pausingAutoRetryStoId === order.sto_id}
                        title="Click before manually working this order in SAP's own UI - stops the automated retry from moving stock at the same time."
                        data-testid="stock-transfer-detail-pause-auto-retry-button"
                      >
                        {pausingAutoRetryStoId === order.sto_id ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}
                        Pause Auto-Retry (Fixing in SAP)
                      </Button>
                      <Button size="sm" onClick={() => onCompleteManualGi(order.sto_id)} disabled={completingGiStoId === order.sto_id} data-testid="stock-transfer-detail-complete-manual-gi-button">
                        {completingGiStoId === order.sto_id ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}
                        I've Completed This in SAP
                      </Button>
                    </>
                  )}
                </div>
              </TimelineNode>
            );
          })()}

          {(() => {
            // Sep 23 2026, user's explicit correction: ERP sync is NOT
            // automatic anymore - it's a deliberate "Sync ERP" click once
            // Goods Issue posts, and Print Delivery Note only unlocks
            // once that sync actually succeeds (see backend: the
            // auto-trigger was removed from _run_goods_issue_job /
            // the healing sweep / complete-manual-gi; only this button's
            // own retry-erp-sync endpoint starts a sync now).
            const giPosted = order.gi_status === "posted";
            const erpStatus = order.erp_portal_status;
            const erpState = !giPosted ? "pending"
              : erpStatus === "synced" ? "done"
              : erpStatus === "failed" ? "failed"
              : erpStatus === "paused" ? "paused"
              : (erpStatus === "syncing" || erpStatus === "retrying") ? "active"
              : "manual";
            return (
              <TimelineNode testId="stock-transfer-detail-erp-status" label="ERP Portal Sync" state={erpState} isLast>
                {erpState === "paused" && <p className="text-xs text-[#667085] mt-0.5">Sync intentionally turned off - no Delivery Challan until it's turned back on.</p>}
                {erpState === "failed" && <p className="text-xs text-[#B42318] mt-0.5">{order.erp_portal_error || "See logs."}</p>}
                {erpState === "done" && <p className="text-xs text-[#667085] mt-0.5">Portal Sale No: {order.erp_sale_no} / {order.erp_sale_noc}</p>}
                {erpState === "manual" && <p className="text-xs text-[#98A2B3] mt-0.5">Ready to sync - Print Delivery Note unlocks once this succeeds.</p>}
                {erpState === "failed" && <p className="text-xs text-[#98A2B3] mt-0.5">No legal Delivery Challan can be printed until this syncs - Serial Number comes from the ERP portal.</p>}
                {erpState === "done" && isAdmin && (
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <Button
                      size="sm" variant="outline" onClick={() => onVerifyErpSync(order.sto_id)}
                      disabled={verifyingErpStoId === order.sto_id} data-testid="stock-transfer-verify-erp-sync-btn"
                    >
                      {verifyingErpStoId === order.sto_id ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : null}
                      Verify ERP Sync
                    </Button>
                  </div>
                )}
                {(erpState === "manual" || erpState === "failed") && (
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <Button size="sm" onClick={() => onRetryErpSync(order.sto_id)} disabled={retryingErpStoId === order.sto_id} data-testid="stock-transfer-retry-erp-sync-btn">
                      {retryingErpStoId === order.sto_id ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : null}
                      {erpState === "failed" ? "Retry ERP Sync" : "Sync ERP"}
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="outline" onClick={() => onManualErpLink(order.sto_id)} data-testid="stock-transfer-manual-erp-link-btn">
                        Link to Existing Challan
                      </Button>
                    )}
                  </div>
                )}
              </TimelineNode>
            );
          })()}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <div><Label className="text-xs font-bold text-[#344054]">Ship-from Site</Label><p data-testid="stock-transfer-detail-ship-from">{order.ship_from_site_id}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Ship-to Site</Label><p data-testid="stock-transfer-detail-ship-to">{order.ship_to_site_id}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Ship-to Location</Label><p data-testid="stock-transfer-detail-ship-to-location">{order.ship_to_location_name || order.ship_to_location_id}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Delivery Priority</Label><p>{order.delivery_priority}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Requested Delivery Date</Label><p>{order.requested_delivery_date}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Transportation Mode</Label><p>{order.transportation_mode || "—"}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Vehicle No.</Label><p>{order.vehicle_no || "—"}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Place Of Supply</Label><p>{order.place_of_supply || "—"}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">G.R No.</Label><p>{order.gr_no || "—"}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Date Of Supply</Label><p>{order.date_of_supply || "—"}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Freight Forwarder</Label><p data-testid="stock-transfer-detail-freight-forwarder">{order.freight_forwarder || "—"}</p></div>
        <div><Label className="text-xs font-bold text-[#344054]">Remark</Label><p data-testid="stock-transfer-detail-remark">{order.remark || "—"}</p></div>
      </div>

      <div className="border border-[#EAECF0] rounded-sm overflow-auto">
        {order.gi_delivery_request_id && (
          <p className="px-2 py-1 text-[11px] text-[#475467] bg-[#F9FAFB] border-b border-[#EAECF0]" data-testid="stock-transfer-detail-items-table-delivery-request">
            Delivery Request in SAP: <span className="font-bold text-[#344054]">{order.gi_delivery_request_id}</span>
            {order.gi_playwright_user && <> &middot; SAP user: <span className="font-bold text-[#344054]">{order.gi_playwright_user}</span></>}
          </p>
        )}
        <table className="w-full text-[11px] border-collapse" data-testid="stock-transfer-detail-items-table">
          <thead>
            <tr>
              {["Line", "Product", "Description", "HSN Code", "Source Warehouse", "Available Qty", "Requested Qty", "Stock Status", "Ship Status"].map((h) => (
                <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-[11px] font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(order.items || []).map((it, idx) => {
              const ship = lineShipStatus(order, it);
              const shipStyle = SHIP_STATUS_STYLE[ship.status] || SHIP_STATUS_STYLE.pending;
              return (
              <tr key={it.line_no ?? idx} data-testid={`stock-transfer-detail-item-${it.product_id}`}>
                <td className="border border-[#D0D5DD] px-2 py-1.5">{it.line_no}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium">{it.product_id}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5">{it.description || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`stock-transfer-detail-hsn-${it.product_id}`}>{it.hsn_code || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5">{it.source_warehouse_name || it.source_warehouse_id}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(it.available_qty)} {it.unit_of_measure}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(it.requested_qty)} {it.unit_of_measure}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5">{it.availability_status}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5">
                  <StatusText tone={shipStyle.tone} title={ship.note || ""} testId={`stock-transfer-detail-ship-status-${it.line_no ?? idx}`}>
                    {shipStyle.label}
                  </StatusText>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};



export default function StockTransferPage() {
  const [items, setItems] = useState([]);
  const [productQuery, setProductQuery] = useState("");
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchWrapperRef = useRef(null);
  const suggestionDebounceRef = useRef(null);

  const [shipToSiteId, setShipToSiteId] = useState("");
  const [shipToSiteOptions, setShipToSiteOptions] = useState([]);
  const [shipToLocationId, setShipToLocationId] = useState("");
  const [shipToLocationOptions, setShipToLocationOptions] = useState([]);
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState(todayISO());
  // GST / E-way bill compliance fields (Aug 2026, user's explicit ask) -
  // mandatory; pushed live to SAP as a Note on the Customer Requirement
  // (see stock_transfer_service.py's _build_gst_note_text).
  const [transportationMode, setTransportationMode] = useState("By Road");
  const [vehicleNo, setVehicleNo] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [grNo, setGrNo] = useState("");
  const [dateOfSupply, setDateOfSupply] = useState(todayISO());
  // Freight Forwarder / Transporter name (Aug 27 2026, user's explicit
  // ask - mandatory) - written to the SAP GST Note AND the legacy ERP
  // portal's own `Trans` field, printed on the Delivery Note + Gate Pass.
  const [freightForwarder, setFreightForwarder] = useState("");
  // Remark (Sep 2 2026, user's explicit ask) - optional free text, shown
  // right after Freight Forwarder everywhere it appears.
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [recentOrders, setRecentOrders] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Sep 7 2026, user's explicit ask: filter row above the Recent Stock
  // Transfer Orders table - all client-side over the already-loaded
  // (max 100, most recent) `recentOrders` list, no backend change needed.
  const [filterStoId, setFilterStoId] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterCreatedBy, setFilterCreatedBy] = useState("all");
  const [filterShipFrom, setFilterShipFrom] = useState("all");
  const [filterShipTo, setFilterShipTo] = useState("all");
  const [filterItem, setFilterItem] = useState("");

  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";

  // "Action Needed" admin panel (Aug 2026, user's explicit ask): when an
  // STO fails because a product was never set up (Planning/Availability/
  // Logistics/Valuation) at the destination site, super_admin/admin users
  // see it here with a one-click "Activate" - no toast, inline result
  // message only per user's ask, since there's no safe dry-run on SAP's
  // side for this write.
  const [notifications, setNotifications] = useState([]);
  const [activatingId, setActivatingId] = useState(null);
  const [activateResults, setActivateResults] = useState({}); // { [notificationId]: {planning_logistics, valuation} }
  const [confirmActivateFor, setConfirmActivateFor] = useState(null);
  const [retryingStoId, setRetryingStoId] = useState(null);

  const loadNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications`);
      setNotifications(data.notifications || []);
    } catch {
      setNotifications([]);
    }
  };

  // Sep 3 2026, user's explicit ask: any user who can create Stock
  // Transfer Orders should see + be able to fix a "site not activated"
  // blocker for their own orders, not just admins.
  useEffect(() => { loadNotifications(); }, []);

  const handleActivate = async (notification) => {
    setActivatingId(notification._id);
    try {
      const { data } = await axios.post(`${API}/admin/material-sites/activate`, {
        product_id: notification.product_id,
        site_id: notification.site_id,
        notification_id: notification._id,
      });
      setActivateResults((prev) => ({ ...prev, [notification._id]: data }));
      if (data.planning_logistics === "ok") loadNotifications();
    } catch (e) {
      setActivateResults((prev) => ({ ...prev, [notification._id]: { planning_logistics: e.response?.data?.detail || "Request failed" } }));
    } finally {
      setActivatingId(null);
      setConfirmActivateFor(null);
    }
  };

  const handleRetryOrder = async (stoId) => {
    setRetryingStoId(stoId);
    try {
      await axios.post(`${API}/stock-transfer/orders/${stoId}/retry`);
      setTimeout(() => { loadRecentOrders(); setRetryingStoId(null); }, 4000);
    } catch {
      setRetryingStoId(null);
    }
  };

  // Sep 23 2026, user's explicit ask - ONE "Retry Posting" action
  // replaces the old separate "Retry Combine Step"/"Retry Goods Issue"
  // buttons (both retried the same automated pipeline, just caught at
  // two different exhaustion points) - backend figures out which
  // resume path applies, see /retry-posting's own docstring.
  const [retryingPostingStoId, setRetryingPostingStoId] = useState(null);
  const handleRetryPosting = async (stoId) => {
    setRetryingPostingStoId(stoId);
    try {
      await axios.post(`${API}/stock-transfer/orders/${stoId}/retry-posting`);
      toast.message("Retrying - will keep checking automatically.");
      loadRecentOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not retry this order.");
    } finally {
      setRetryingPostingStoId(null);
    }
  };

  const [completingGiStoId, setCompletingGiStoId] = useState(null);
  const handleCompleteManualGi = async (stoId) => {
    setCompletingGiStoId(stoId);
    try {
      await axios.post(`${API}/stock-transfer/orders/${stoId}/complete-manual-gi`);
      toast.success("Goods Issue confirmed in SAP - Stock Transfer Order complete.");
      loadRecentOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm the manual Goods Issue.");
    } finally {
      setCompletingGiStoId(null);
    }
  };

  const [pausingAutoRetryStoId, setPausingAutoRetryStoId] = useState(null);
  const handlePauseAutoRetry = async (stoId) => {
    setPausingAutoRetryStoId(stoId);
    try {
      await axios.post(`${API}/stock-transfer/orders/${stoId}/pause-auto-retry`);
      toast.success("Automation paused for 10 minutes - safe to work this order directly in SAP now.");
      loadRecentOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pause auto-retry for this order.");
    } finally {
      setPausingAutoRetryStoId(null);
    }
  };


  const [stoppingGiStoId, setStoppingGiStoId] = useState(null);
  const handleForceStopGi = async (stoId) => {
    setStoppingGiStoId(stoId);
    try {
      await axios.post(`${API}/stock-transfer/orders/${stoId}/force-stop-gi`);
      toast.message("Switched to manual - a Delivery may already exist in SAP for this order, check before retrying.");
      setTimeout(() => { loadRecentOrders(); setStoppingGiStoId(null); }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch this order to manual.");
      setStoppingGiStoId(null);
    }
  };

  const [retryingErpStoId, setRetryingErpStoId] = useState(null);
  const handleRetryErpSync = async (stoId) => {
    setRetryingErpStoId(stoId);
    try {
      await axios.post(`${API}/stock-transfer/orders/${stoId}/retry-erp-sync`);
      toast.message("Retrying ERP Portal sync (tries primary server, then fallback automatically).");
      setTimeout(() => { loadRecentOrders(); setRetryingErpStoId(null); }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not retry ERP Portal sync.");
      setRetryingErpStoId(null);
    }
  };

  const handleManualErpLink = async (stoId) => {
    const sale_no = window.prompt("Sale_No of the Delivery Challan that already exists in the ERP for this order?");
    if (!sale_no) return;
    const sale_noc = window.prompt("Sale_Noc for that same Challan?");
    if (!sale_noc) return;
    try {
      await axios.post(`${API}/stock-transfer/orders/${stoId}/manual-erp-link`, { sale_no: Number(sale_no), sale_noc: Number(sale_noc) });
      toast.success(`Linked to existing Challan Sale_No ${sale_no}.`);
      loadRecentOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link this order to that Challan.");
    }
  };

  // Oct 2026, real incident STO-000701 (user's explicit ask, "changes
  // status done only when its actually created in erp db") - lets an
  // admin re-check ANY already-"synced" order's stored Sale_No against
  // the live ERP DB, in case it was synced before create_delivery_challan
  // started verifying this itself. Flips back to "failed" (Retry button
  // works again) if the Sale_No turns out to be phantom.
  const [verifyingErpStoId, setVerifyingErpStoId] = useState(null);
  const handleVerifyErpSync = async (stoId) => {
    setVerifyingErpStoId(stoId);
    try {
      const { data } = await axios.post(`${API}/stock-transfer/orders/${stoId}/verify-erp-sync`);
      if (data.verified) {
        toast.success(`Confirmed - Sale No ${data.sale_no}/${data.sale_noc} really exists in the ERP portal.`);
      } else {
        toast.error(`Sale No ${data.sale_no}/${data.sale_noc} was NOT actually saved in the ERP - reset to Failed so you can retry.`);
      }
      loadRecentOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify this order's ERP sync.");
    } finally {
      setVerifyingErpStoId(null);
    }
  };

  // 2x-safety confirmation before the real, irreversible live SAP write
  // (user's explicit ask, Aug 2026): clicking "Create" first opens a
  // summary dialog - nothing is submitted until the user explicitly
  // confirms a SECOND time inside that dialog. The dialog then shows a
  // real (not simulated) step-by-step progress bar driven by the
  // background job's own `step` field.
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [sapSubmitPhase, setSapSubmitPhase] = useState(null); // null | "submitting" | "done" | "failed"
  const [sapSubmitMessage, setSapSubmitMessage] = useState(null);
  const [stepStatuses, setStepStatuses] = useState({}); // { [stepKey]: "pending" | "active" | "done" | "failed" }

  // Sep 21 2026, user's explicit ask - split Create into 2 explicit
  // steps: "Validate STO" (zero side effects - live stock/valuation/
  // activation pre-check) must pass clean before "Review & Create STO"
  // is even clickable. Any edit to items/destination/date invalidates
  // a previous pass, forcing re-validation (see the effect below).
  const [validating, setValidating] = useState(false);
  const [sapValidated, setSapValidated] = useState(false);
  const [validationIssues, setValidationIssues] = useState([]);
  const [showValidationErrorBox, setShowValidationErrorBox] = useState(false);

  // Sep 22 2026, 4-button STO redesign, Button 1 ("Validate + Stage
  // Stock" - STO_4BUTTON_REDESIGN.md section 2). `stagingId` identifies
  // the stock_transfer_staging record backing whatever is currently
  // sitting in HOLD; `stagedItems` is the per-line movement summary the
  // user reviews before Confirm/Cancel. No SAP order exists until
  // Confirm fires Button 2.
  const [stagingId, setStagingId] = useState(null);
  const [stagedItems, setStagedItems] = useState([]);
  const [stagingGacId, setStagingGacId] = useState(null);
  const [showStagingDetails, setShowStagingDetails] = useState(true);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [addingProductId, setAddingProductId] = useState(null);
  const [draftInfo, setDraftInfo] = useState(null);
  const [restoringDraft, setRestoringDraft] = useState(false);
  const [retryingCheck, setRetryingCheck] = useState(null);
  const [cancelingStaging, setCancelingStaging] = useState(false);
  const [confirmingStaging, setConfirmingStaging] = useState(false);
  const stagingIdRef = useRef(null);
  useEffect(() => { stagingIdRef.current = stagingId; }, [stagingId]);

  useEffect(() => {
    setSapValidated(false);
    // Editing anything after a successful stage invalidates it - best-
    // effort auto-reverse whatever was staged so stock never sits
    // orphaned in HOLD just because the user tweaked a line afterward.
    if (stagingIdRef.current) {
      axios.post(`${API}/stock-transfer/staging/${stagingIdRef.current}/cancel`).catch(() => {});
      stagingIdRef.current = null;
    }
    setStagingId(null);
    setStagedItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, shipToSiteId, shipToLocationId, requestedDeliveryDate]);

  // Prod bug fix (real report - dialog stuck open showing "Validation
  // found 0 issues") - the per-category "Retry" button lives INSIDE
  // this same error dialog; when a retry clears the LAST remaining
  // issue, validationIssues empties out but nothing ever closed the
  // dialog, leaving it open with nothing to show. Auto-close the
  // instant every issue is resolved, regardless of which code path
  // (retry, or a fresh validate) got it there.
  useEffect(() => {
    if (showValidationErrorBox && validationIssues.length === 0) {
      setShowValidationErrorBox(false);
    }
  }, [validationIssues, showValidationErrorBox]);

  // Oct 2026 fix (production latency real incident - SAP calls take
  // 37-53s each on production's network path, freezing "Validate +
  // Stage Stock"/per-category Retry for 1-2 minutes) - /stock-transfer/
  // stage and /stock-transfer/validate now return a job_id instantly and
  // do the real SAP work in the background; this polls for the result
  // instead of awaiting one long HTTP request (same pattern already used
  // for STO order creation, see pollSapJob below).
  const pollJobResult = async (statusUrlBase, jobId, timeoutMs = 3 * 60 * 1000) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const { data: job } = await axios.get(`${statusUrlBase}/${jobId}`);
      if (job.status === "done") return job.result;
      if (job.status === "failed") throw new Error(job.error || "Request failed");
      await new Promise((r) => setTimeout(r, 2000));
    }
    throw new Error("This is taking longer than expected - check back shortly, it may still complete in the background.");
  };

  const runValidateAndStage = async () => {
    const validationError = runValidation();
    setFormError(validationError);
    if (validationError) { toast.error(validationError); return; }
    setValidating(true);
    try {
      const { data: jobStart } = await axios.post(`${API}/stock-transfer/stage`, {
        ship_to_site_id: shipToSiteId,
        requested_delivery_date: requestedDeliveryDate,
        items: items.map((i) => ({
          product_id: i.product_id,
          source_warehouse_id: i.source_warehouse_id,
          requested_qty: Number(i.requested_qty),
        })),
      });
      const data = await pollJobResult(`${API}/stock-transfer/stage/status`, jobStart.job_id);
      if (data.ok && data.staging_id) {
        setSapValidated(true);
        setStagingId(data.staging_id);
        setStagedItems(data.staged_items || []);
        setStagingGacId(data.gac_id || null);
        setShowStagingDetails(true);
        toast.success((data.staged_items || []).length > 0
          ? "Validated and stock staged in HOLD - review below, then Confirm or Cancel."
          : "Validated - this site has no HOLD warehouse, nothing was moved. Review below, then Confirm or Cancel.");
      } else {
        setSapValidated(false);
        setStagingId(null);
        setStagedItems(data.staged_items || []);
        setValidationIssues(data.issues || []);
        setShowValidationErrorBox(true);
      }
    } catch (e) {
      setSapValidated(false);
      const msg = e?.response?.data?.detail || e?.message || "Could not validate/stage this Stock Transfer Order.";
      setValidationIssues([{ product_id: null, field: "sap", level: "error", message: msg }]);
      setShowValidationErrorBox(true);
    } finally {
      setValidating(false);
    }
  };

  // Per-category "Retry" (user's explicit choice: separate retry
  // buttons per sub-step) - re-runs ONLY that one check via
  // /stock-transfer/validate's `only_checks`, without re-running the
  // others or re-staging stock.
  const retryCheckCategory = async (category) => {
    setRetryingCheck(category);
    try {
      const { data: jobStart } = await axios.post(`${API}/stock-transfer/validate`, {
        ship_to_site_id: shipToSiteId,
        requested_delivery_date: requestedDeliveryDate,
        items: items.map((i) => ({
          product_id: i.product_id,
          source_warehouse_id: i.source_warehouse_id,
          requested_qty: Number(i.requested_qty),
        })),
        only_checks: [category],
      });
      const data = await pollJobResult(`${API}/stock-transfer/validate/status`, jobStart.job_id);
      const stillFailing = data.issues || [];
      setValidationIssues((prev) => [...prev.filter((iss) => iss.field !== category), ...stillFailing]);
      if (stillFailing.length === 0) toast.success(`${CHECK_LABELS[category] || category} check now passes - re-run "Validate + Stage Stock" once every issue is cleared.`);
      else toast.error(`${CHECK_LABELS[category] || category} still has issues.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || `Could not retry the ${category} check.`);
    } finally {
      setRetryingCheck(null);
    }
  };

  const handleCancelStaging = async () => {
    if (!stagingId) return;
    setCancelingStaging(true);
    try {
      await axios.post(`${API}/stock-transfer/staging/${stagingId}/cancel`);
      toast.message(stagedItems.length > 0
        ? "Cancelled - the staged stock stays in HOLD as-is (it's automatically reused next time you stage this same item, nothing wasted)."
        : "Cancelled - no stock was ever moved for this order, nothing to reverse.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reverse staged stock - it may already be gone.");
    } finally {
      stagingIdRef.current = null;
      setStagingId(null);
      setStagedItems([]);
      setSapValidated(false);
      setCancelingStaging(false);
    }
  };

  const handleConfirmStaging = async () => {
    if (!stagingId) return;
    const validationError = runValidation();
    setFormError(validationError);
    if (validationError) { toast.error(validationError); return; }
    setConfirmingStaging(true);
    try {
      await axios.post(`${API}/stock-transfer/staging/${stagingId}/confirm`);
      setShowConfirmDialog(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm staged stock.");
    } finally {
      setConfirmingStaging(false);
    }
  };

  const shipFromSiteId = items.find((i) => i.ship_from_site_id)?.ship_from_site_id || "";

  const refreshItemStock = async (itemKey, productId) => {
    // Per-item "Refresh" (Sep 2026, user's explicit ask) - available as
    // soon as the item is picked, no site needed first. Scoped to just
    // this one product across every site/warehouse - a live SAP pull
    // this narrow takes ~11-17s end to end (verified), well under the
    // ~12s/site or 60s+/company-wide full refresh this replaces.
    setItems((prev) => prev.map((i) => (i.key === itemKey ? { ...i, refreshing: true } : i)));
    try {
      await axios.post(`${API}/stock-transfer/refresh-item-stock`, null, { params: { product_id: productId } });
      const { data: inv } = await axios.get(`${API}/stock-transfer/inventory`, { params: { product_id: productId, include_non_usable: true } });
      setItems((prev) => prev.map((i) => {
        if (i.key !== itemKey) return i;
        const freshLocations = inv.locations || [];
        // Bug fix (Sep 18 2026, real user report): the "Available Qty"
        // column used to keep showing whatever qty was captured at the
        // moment the warehouse was picked/suggested - after a live
        // Refresh updates `locations` (the dropdown's own source), the
        // already-selected warehouse's `available_qty` never re-synced,
        // so the dropdown label and the Available Qty column visibly
        // disagreed. Re-look up the currently selected warehouse in the
        // freshly refreshed locations and sync available_qty to match.
        const selectedLoc = i.source_warehouse_id ? freshLocations.find((l) => l.warehouse_id === i.source_warehouse_id) : null;
        return {
          ...i,
          locations: freshLocations,
          hsn_code: inv.hsn_code,
          available_qty: selectedLoc ? selectedLoc.qty : i.available_qty,
        };
      }));
      toast.success(`${productId} stock refreshed live from SAP.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not refresh item stock.");
    } finally {
      setItems((prev) => prev.map((i) => (i.key === itemKey ? { ...i, refreshing: false } : i)));
    }
  };

  const loadRecentOrders = async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) setLoadingRecent(true);
    try {
      const { data } = await axios.get(`${API}/stock-transfer/orders`);
      // Sep 23 2026, user's explicit ask ("page still refreshing, is
      // this required???") - a background poll (every 5s while any
      // order has a running job) was blindly re-triggering the
      // "Loading..." state every single tick, blanking out the whole
      // table even when nothing actually changed. Only the FIRST load
      // shows the loading placeholder now; background refreshes update
      // silently, and only when the data genuinely differs.
      setRecentOrders((prev) => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
    } catch {
      if (!isBackgroundRefresh) setRecentOrders([]);
    } finally {
      if (!isBackgroundRefresh) setLoadingRecent(false);
    }
  };

  useEffect(() => { loadRecentOrders(); }, []);

  // Sep 7 2026, user's explicit ask: dropdown options + filtered list for
  // the Recent Stock Transfer Orders filter row - derived straight from
  // whatever's already loaded, no extra API call.
  const distinctCreatedBy = [...new Set(recentOrders.map((o) => o.created_by).filter(Boolean))].sort();
  const distinctShipFrom = [...new Set(recentOrders.map((o) => o.ship_from_site_id).filter(Boolean))].sort();
  const distinctShipTo = [...new Set(recentOrders.map((o) => o.ship_to_site_id).filter(Boolean))].sort();
  const filteredRecentOrders = recentOrders.filter((o) => {
    if (filterStoId && !o.sto_id.toLowerCase().includes(filterStoId.trim().toLowerCase())) return false;
    if (filterDate && (o.created_at || "").slice(0, 10) !== filterDate) return false;
    if (filterCreatedBy !== "all" && o.created_by !== filterCreatedBy) return false;
    if (filterShipFrom !== "all" && o.ship_from_site_id !== filterShipFrom) return false;
    if (filterShipTo !== "all" && o.ship_to_site_id !== filterShipTo) return false;
    if (filterItem) {
      const q = filterItem.trim().toLowerCase();
      const matches = (o.items || []).some((it) => (it.product_id || "").toLowerCase().includes(q) || (it.description || "").toLowerCase().includes(q));
      if (!matches) return false;
    }
    return true;
  });
  const hasActiveRecentFilters = !!(filterStoId || filterDate || filterCreatedBy !== "all" || filterShipFrom !== "all" || filterShipTo !== "all" || filterItem);
  const clearRecentFilters = () => {
    setFilterStoId(""); setFilterDate(""); setFilterCreatedBy("all"); setFilterShipFrom("all"); setFilterShipTo("all"); setFilterItem("");
  };

  // Companion to the sync-fix above - without this, the list itself
  // never refreshes on its own either (no other periodic poll exists),
  // so a long-running background job just sits stale until the user
  // manually reloads. Auto-refresh only while something is actually
  // in-flight.
  // Bug fix (real user report, screenshot showing the detail modal stuck
  // on a spinning "ERP Portal: syncing..." even though the backend sync
  // had actually already finished) - this polling gate only ever looked
  // at `gi_job_running`, which flips to false the MOMENT Goods Issue
  // posts, i.e. exactly when the separate background ERP sync job starts
  // running. So polling stopped right as ERP sync began, freezing the
  // modal on "syncing..." forever until a full manual page reload. Now
  // also polls while any order has ERP sync genuinely in flight (Goods
  // Issue posted, but erp_portal_status not yet a terminal synced/failed).
  const hasRunningGiJob = recentOrders.some((o) => o.gi_job_running);
  const hasRunningErpSync = recentOrders.some((o) => o.gi_status === "posted" && !["synced", "failed", "paused"].includes(o.erp_portal_status));
  useEffect(() => {
    if (!hasRunningGiJob && !hasRunningErpSync) return;
    const id = setInterval(() => loadRecentOrders(true), 5000);
    return () => clearInterval(id);
  }, [hasRunningGiJob, hasRunningErpSync]);

  // Real bug (user's screenshot, Sep 2026): the detail modal is a static
  // snapshot from whatever row was clicked - it never refreshed on its
  // own, so if a background retry re-acquired a DIFFERENT pooled SAP
  // login (itadmin/STOREBOT1/STOREBOT2 - by design, not sticky across
  // retries) after the modal was opened, it kept showing the stale
  // username while the list table below (refreshed by loadRecentOrders)
  // already showed the current one. Keep it in sync with whatever the
  // list already has, every time the list refreshes.
  useEffect(() => {
    if (!selectedOrder) return;
    const updated = recentOrders.find((o) => o.sto_id === selectedOrder.sto_id);
    if (updated && updated !== selectedOrder) setSelectedOrder(updated);
  }, [recentOrders]);

  // Direct fix (user's follow-up report: the modal still didn't update
  // live even with the cross-sync above + the list's own polling fixed -
  // that path only refreshes the modal WHEN the list itself happens to
  // poll, and depends on the list's own running-job gate correctly
  // catching every in-flight state; too indirect). While the detail
  // modal is open, poll its OWN single-order endpoint directly every 4s
  // (already used elsewhere for the just-created-order progress view -
  // see GET /stock-transfer/orders/{sto_id} above) and push the fresh
  // copy into both `selectedOrder` and the matching `recentOrders` row,
  // so it reflects live status without needing to close and reopen it.
  useEffect(() => {
    if (!selectedOrder) return;
    const stoId = selectedOrder.sto_id;
    const id = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/stock-transfer/orders/${stoId}`);
        // Sep 23 2026, user's explicit ask ("page still keeps refreshing,
        // needs to stop, can it be more granular") - only push a new
        // object into state (triggering a re-render) when something
        // actually changed, instead of blindly replacing it every 4s
        // even when the fetch came back byte-for-byte identical.
        setSelectedOrder((prev) => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
        setRecentOrders((prev) => prev.map((o) => (o.sto_id === stoId ? (JSON.stringify(o) === JSON.stringify(data) ? o : data) : o)));
      } catch {
        // transient network hiccup - next tick retries, nothing to show the user for this
      }
    }, 4000);
    return () => clearInterval(id);
  }, [selectedOrder?.sto_id]);

  // Ship-to Site options depend on the (derived) Ship-from Site - refetch
  // whenever the first line item's warehouse pick resolves/changes it.
  // User's explicit ask: once Ship-to Site/Location are picked they should
  // stay valid for every item added afterwards, not need re-picking - so
  // only clear them if they've actually become INVALID for the (possibly
  // changed) Ship-from Site, never just because something else changed.
  useEffect(() => {
    if (!shipFromSiteId) {
      setShipToSiteOptions([]);
      setShipToSiteId("");
      return;
    }
    axios.get(`${API}/stock-transfer/ship-to-sites`, { params: { ship_from_site_id: shipFromSiteId } })
      .then(({ data }) => {
        const sites = data.sites || [];
        setShipToSiteOptions(sites);
        setShipToSiteId((prev) => (prev && !sites.includes(prev) ? "" : prev));
      })
      .catch(() => setShipToSiteOptions([]));
  }, [shipFromSiteId]);

  // Ship-to Location options depend on the chosen Ship-to Site.
  useEffect(() => {
    if (!shipToSiteId) { setShipToLocationOptions([]); setShipToLocationId(""); return; }
    axios.get(`${API}/stock-transfer/locations`, { params: { site_id: shipToSiteId } })
      .then(({ data }) => setShipToLocationOptions(data.warehouses || []))
      .catch(() => setShipToLocationOptions([]));
  }, [shipToSiteId]);

  useEffect(() => {
    if (suggestionDebounceRef.current) clearTimeout(suggestionDebounceRef.current);
    const q = productQuery.trim();
    if (q.length < 2) { setProductSuggestions([]); setSearchingProducts(false); return; }
    setSearchingProducts(true);
    suggestionDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/products/search`, { params: { q, limit: 10 } });
        setProductSuggestions(data);
      } catch {
        setProductSuggestions([]);
      } finally {
        setSearchingProducts(false);
      }
    }, 250);
    return () => clearTimeout(suggestionDebounceRef.current);
  }, [productQuery]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const addItem = async (product_id, description, initialQty) => {
    if (items.some((i) => i.product_id === product_id)) {
      toast.error(`${product_id} is already in the line item list.`);
      return null;
    }
    // Sep 23 2026, user's explicit ask ("takes ages, nothing shows on
    // screen that its happening") - the search box/dropdown used to
    // clear INSTANTLY on click, leaving a blank gap with zero feedback
    // while this awaited. Backend itself responds in well under 1s -
    // the bug was purely missing UI feedback, not real latency. Now
    // the dropdown stays visible with a spinner on the clicked row
    // until the add actually completes.
    setAddingProductId(product_id);
    try {
      const { data } = await axios.get(`${API}/stock-transfer/inventory`, { params: { product_id, include_non_usable: true } });
      const line = emptyLine({ ...data, description: data.description || description });
      if (initialQty != null) line.requested_qty = String(initialQty);
      setItems((prev) => [...prev, line]);
      fetchSuggestion(line.key, product_id);
      setProductQuery("");
      setProductSuggestions([]);
      setShowSuggestions(false);
      return line;
    } catch {
      toast.error(`Could not check inventory for ${product_id}.`);
      return null;
    } finally {
      setAddingProductId(null);
    }
  };

  const fetchSuggestion = async (key, product_id, shipTo = shipToSiteId) => {
    try {
      const { data } = await axios.get(`${API}/stock-transfer/suggest-source`, {
        params: { product_id, ship_to_site_id: shipTo || undefined },
      });
      setItems((prev) => prev.map((i) => (i.key === key ? { ...i, suggestion: data } : i)));
    } catch {
      // purely advisory - silently skip on failure
    }
  };

  // Ship-to Site changing can invalidate/refresh every line's suggestion
  // (a warehouse at the new Ship-to Site should never be suggested).
  useEffect(() => {
    items.forEach((i) => fetchSuggestion(i.key, i.product_id, shipToSiteId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipToSiteId]);

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  // Sep 23 2026, user's explicit ask ("save this STO in draft, 1 hour
  // limit, so I don't have to fill everything again") - checks once on
  // load whether THIS user has an unsaved draft from a recent failed
  // Validate + Stage attempt; only ever offered, never auto-applied.
  useEffect(() => {
    axios.get(`${API}/stock-transfer/draft`).then(({ data }) => {
      if (data.draft) setDraftInfo(data);
    }).catch(() => {});
  }, []);

  const discardDraft = () => {
    setDraftInfo(null);
    axios.delete(`${API}/stock-transfer/draft`).catch(() => {});
  };

  const restoreDraftIntoForm = async () => {
    if (!draftInfo?.draft) return;
    setRestoringDraft(true);
    try {
      const draft = draftInfo.draft;
      if (draft.ship_to_site_id) setShipToSiteId(draft.ship_to_site_id);
      if (draft.requested_delivery_date) setRequestedDeliveryDate(draft.requested_delivery_date);
      for (const it of draft.items || []) {
        const line = await addItem(it.product_id, it.product_id, it.requested_qty);
        if (line && it.source_warehouse_id) chooseWarehouse(line.key, it.source_warehouse_id);
      }
      toast.success("Draft restored - review the items below.");
    } finally {
      setRestoringDraft(false);
      discardDraft();
    }
  };

  const chooseWarehouse = (key, warehouseId) => {
    setItems((prev) => prev.map((i) => {
      if (i.key !== key) return i;
      const loc = i.locations.find((l) => l.warehouse_id === warehouseId);
      if (loc && loc.is_usable === false) return i; // Inspection/Restricted rows are shown but never selectable
      return {
        ...i,
        source_warehouse_id: warehouseId,
        ship_from_site_id: loc ? loc.site_id : "",
        available_qty: loc ? loc.qty : null,
        error: null,
      };
    }));
  };

  const applySuggestion = (key) => {
    setItems((prev) => prev.map((i) => {
      if (i.key !== key || !i.suggestion?.suggested) return i;
      const s = i.suggestion.suggested;
      return { ...i, source_warehouse_id: s.warehouse_id, ship_from_site_id: s.site_id, available_qty: s.qty, error: null };
    }));
  };

  const setRequestedQty = (key, value) => {
    setItems((prev) => prev.map((i) => {
      if (i.key !== key) return i;
      const qty = value === "" ? "" : Number(value);
      let error = null;
      if (value !== "" && (isNaN(qty) || qty <= 0)) error = "Requested Quantity must be greater than zero.";
      else if (i.available_qty != null && qty > i.available_qty) {
        error = "Insufficient Stock. Please enter a quantity equal to or less than the available inventory.";
      }
      return { ...i, requested_qty: value, error };
    }));
  };

  const runValidation = () => {
    if (items.length === 0) return "Select at least one item.";
    for (const i of items) {
      if (!i.source_warehouse_id) return `${i.product_id}: Source Warehouse is required.`;
      if (!i.requested_qty || !Number.isFinite(Number(i.requested_qty)) || Number(i.requested_qty) <= 0) return `${i.product_id}: Requested Quantity must be greater than zero.`;
      if (i.available_qty != null && Number(i.requested_qty) > i.available_qty) return `${i.product_id}: Insufficient Stock. Please enter a quantity equal to or less than the available inventory.`;
    }
    const sites = new Set(items.map((i) => i.ship_from_site_id));
    if (sites.size > 1) return "All line items must ship from the same Ship-from Site - remove the mismatched item(s) or create a separate order.";
    if (!shipToSiteId) return "Ship-to Site is required.";
    if (shipToSiteId === shipFromSiteId) return "Ship-to Site cannot be the same as Ship-from Site.";
    if (!shipToLocationId) return "Ship-to Location is required.";
    if (!requestedDeliveryDate) return "Requested Delivery Date is required.";
    if (requestedDeliveryDate < todayISO()) return "Requested Delivery Date cannot be earlier than today.";
    // Reverted per user's explicit follow-up ask (Sep 18 2026): mandatory
    // again - never written to SAP (kept app-only / pushed to the legacy
    // ERP portal only where it has a matching column, see
    // stock_transfer_service.py's create_stock_transfer_order).
    if (!transportationMode) return "Transportation Mode is required.";
    if (!vehicleNo.trim()) return "Vehicle No. is required.";
    if (!placeOfSupply.trim()) return "Place Of Supply is required.";
    if (!grNo.trim()) return "G.R No. is required.";
    if (!dateOfSupply) return "Date Of Supply is required.";
    if (!freightForwarder.trim()) return "Freight Forwarder is required.";
    return null;
  };

  const applyJobStepStatuses = (job) => {
    setStepStatuses((prev) => {
      if (job.status === "done") return { ...prev, check: "done", create: "done", erp_sync: prev.erp_sync || "active", post_goods_issue: prev.post_goods_issue || "active" };
      if (job.status === "failed") {
        if (job.step === "creating") return { ...prev, check: "done", create: "failed" };
        return { ...prev, check: "failed", create: "pending" };
      }
      if (job.step === "creating") return { ...prev, check: "done", create: "active" };
      return { ...prev, check: "active", create: "pending" };
    });
  };

  // Goods Issue progress (Aug 27 2026) - polled by sto_id (not the job_id
  // above, which only covers the fast Validate/Check/Create steps) once
  // the order is created in SAP. Keeps updating the 4th step + a live
  // status line until posted/failed/timed-out, but never blocks the
  // dialog from being closed - the actual retry loop runs server-side
  // regardless of whether this tab is open.
  //
  // `createdOrderLive` holds the FULL order doc (not just gi_status/
  // erp_portal_status) so this dialog can render the exact same
  // <OrderDetailBody> the detail modal uses (user's explicit ask, Aug
  // 2026: "the dialog that opens when I create the sto should be same
  // as the detail dialog") - one shared source of truth for what an
  // order's live status looks like, instead of two hand-written copies
  // that can drift apart.
  const [createdOrderLive, setCreatedOrderLive] = useState(null);
  const giPollStopRef = useRef(false);
  const pollGiStatus = (stoId) => {
    giPollStopRef.current = false;
    const tick = async () => {
      if (giPollStopRef.current) return;
      try {
        const { data } = await axios.get(`${API}/stock-transfer/orders/${stoId}`);
        setCreatedOrderLive(data);
        const erpDone = ["synced", "paused", "failed"].includes(data.erp_portal_status);
        // Sep 25 2026 fix (real user report - the top step pill for "Sync
        // to ERP Portal" was spinning as if actively running even before
        // Goods Issue had posted, i.e. before ERP sync could possibly have
        // started) - only "active" once GI is actually posted; "pending"
        // (plain idle circle) before that.
        setStepStatuses((prev) => ({
          ...prev,
          erp_sync: erpDone ? (data.erp_portal_status === "failed" ? "failed" : "done") : (data.gi_status === "posted" ? "active" : "pending"),
        }));
        const done = data.gi_status === "posted";
        const failed = data.gi_status === "failed" || data.gi_status === "not_found_timeout";
        const manual = data.gi_status === "awaiting_manual_gi";
        setStepStatuses((prev) => ({ ...prev, post_goods_issue: done ? "done" : failed ? "failed" : manual ? "active" : "active" }));
        // User's explicit ask (Sep 21 2026) - once EVERYTHING (Goods Issue +
        // ERP Portal sync) has reached a terminal state, auto-close this
        // dialog instead of leaving it open showing a finished order.
        // Previously stopped polling as soon as `done` was true, even if
        // ERP sync was still mid-retry on that same tick - freezing its
        // badge on "syncing..." forever since no later tick would ever
        // update it again. Now waits for BOTH to be terminal before
        // stopping, and only auto-closes on the genuine full-success path.
        // Sep 25 2026 fix (user's explicit ask): used to auto-close 1.2s
        // after both Goods Issue AND ERP sync finished - too fast for the
        // user to actually click the "Print Delivery Note" button that
        // OrderDetailBody already shows once erp_portal_status="synced".
        // Now just stops polling and leaves the dialog open with its
        // existing "Close" button below for the user to dismiss manually
        // once they're done (e.g. after printing).
        if (done && erpDone) { loadRecentOrders(); giPollStopRef.current = true; return; }
        if (failed || manual) { loadRecentOrders(); return; }
      } catch { /* keep polling - a transient blip here shouldn't stop the loop */ }
      if (!giPollStopRef.current) setTimeout(tick, 4000);
    };
    tick();
  };

  const pollSapJob = (jobId) => {
    const start = Date.now();
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/stock-transfer/orders/sap-status/${jobId}`);
        applyJobStepStatuses(data);
        if (data.status === "done") {
          setSapSubmitPhase("done");
          setSapSubmitMessage(`Created in SAP as ${formatSapId(data.result?.sap_order_id) || "—"}. Now posting Goods Issue...`);
          toast.success(`Stock Transfer Order created in SAP (${formatSapId(data.result?.sap_order_id) || "—"}).`);
          setItems([]);
          setShipToSiteId(""); setShipToLocationId(""); setRequestedDeliveryDate(todayISO()); setFormError(null);
          setVehicleNo(""); setPlaceOfSupply(""); setGrNo(""); setDateOfSupply(todayISO()); setTransportationMode("By Road"); setFreightForwarder(""); setRemark("");
          loadRecentOrders();
          if (data.sto_id) pollGiStatus(data.sto_id);
          return;
        }
        if (data.status === "failed") {
          setSapSubmitPhase("failed");
          setSapSubmitMessage(cleanSapMessage(data.error) || "SAP rejected this order.");
          toast.error(cleanSapMessage(data.error) || "SAP rejected this Stock Transfer Order.");
          loadRecentOrders();
          return;
        }
        if (Date.now() - start > 3 * 60 * 1000) {
          setSapSubmitPhase("failed");
          setSapSubmitMessage("Still waiting on SAP after 3 minutes - check the order's row/detail view shortly.");
          loadRecentOrders();
          return;
        }
        setTimeout(tick, 3000);
      } catch {
        setTimeout(tick, 3000);
      }
    };
    tick();
  };

  const confirmAndSubmit = async () => {
    setSubmitting(true);
    setSapSubmitPhase("submitting");
    setSapSubmitMessage(null);
    setStepStatuses({ validate: "active", check: "pending", create: "pending", erp_sync: "pending", post_goods_issue: "pending" });
    setCreatedOrderLive(null);
    try {
      const payload = {
        ship_to_site_id: shipToSiteId,
        ship_to_location_id: shipToLocationId,
        requested_delivery_date: requestedDeliveryDate,
        transportation_mode: transportationMode,
        vehicle_no: vehicleNo.trim(),
        place_of_supply: placeOfSupply.trim(),
        gr_no: grNo.trim(),
        date_of_supply: dateOfSupply,
        freight_forwarder: freightForwarder.trim(),
        remark: remark.trim(),
        staging_id: stagingId,
        items: items.map((i) => ({
          product_id: i.product_id,
          source_warehouse_id: i.source_warehouse_id,
          requested_qty: Number(i.requested_qty),
        })),
      };
      const { data } = await axios.post(`${API}/stock-transfer/orders`, payload);
      setStepStatuses({ validate: "done", check: "active", create: "pending", erp_sync: "pending", post_goods_issue: "pending" });
      toast.message(`Stock Transfer Order ${data.sto_id} saved - submitting live to SAP...`);
      loadRecentOrders();
      if (data.sap_job_id) pollSapJob(data.sap_job_id);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create the Stock Transfer Order.";
      setFormError(msg);
      toast.error(msg);
      setSapSubmitPhase(null);
      setStepStatuses({ validate: "failed", check: "pending", create: "pending", erp_sync: "pending", post_goods_issue: "pending" });
    } finally {
      setSubmitting(false);
      stagingIdRef.current = null;
      setStagingId(null);
      setStagedItems([]);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]" data-testid="stock-transfer-page">
      <Toaster position="top-right" richColors />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Inter-Plant Stock Transfer</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0"><NavTabs /></div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <main className="flex-1 overflow-auto max-w-[1400px] w-full mx-auto px-6 py-6 space-y-4">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#1D2939]">Inter-Plant Stock Transfer</h1>
          <p className="text-sm text-[#667085] mt-0.5">Create a Stock Transfer Order to move inventory between sites - submitted live to SAP immediately after you confirm.</p>
        </div>

        {/* Draft restore banner (Sep 23 2026, user's explicit ask) */}
        {draftInfo?.draft && (
          <div className="bg-[#FFFAEB] border border-[#FEC84B] rounded-sm p-3 flex items-center justify-between gap-3" data-testid="stock-transfer-draft-banner">
            <p className="text-sm text-[#93370D]">
              You have an unsaved draft from a failed attempt {Math.max(1, Math.round(draftInfo.age_seconds / 60))} min ago - restore it instead of re-typing everything?
            </p>
            <div className="flex gap-2 shrink-0">
              <Button type="button" size="sm" variant="outline" onClick={discardDraft} data-testid="stock-transfer-draft-discard-button">Discard</Button>
              <Button type="button" size="sm" onClick={restoreDraftIntoForm} disabled={restoringDraft} data-testid="stock-transfer-draft-restore-button">
                {restoringDraft && <CircleNotch size={13} className="animate-spin mr-1.5" />}
                {restoringDraft ? "Restoring..." : "Restore Draft"}
              </Button>
            </div>
          </div>
        )}

        {/* Item search */}
        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3">
          <h3 className="font-heading text-xs font-bold text-[#1D2939] uppercase tracking-wide">1. Select Item(s)</h3>
          <div className="relative max-w-md" ref={searchWrapperRef}>
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
            <Input
              value={productQuery}
              onChange={(e) => { setProductQuery(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Search Product ID or description..."
              className="pl-8"
              data-testid="stock-transfer-product-search-input"
            />
            {searchingProducts && (
              <CircleNotch size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#98A2B3] animate-spin" data-testid="stock-transfer-product-search-loading" />
            )}
            {showSuggestions && productSuggestions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-[#D0D5DD] rounded-sm shadow-lg max-h-64 overflow-y-auto" data-testid="stock-transfer-product-suggestions">
                {productSuggestions.map((s) => (
                  <button
                    key={s.product_id}
                    type="button"
                    disabled={!!addingProductId}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-[#F9FAFB] border-b border-[#EAECF0] last:border-0 disabled:opacity-60 disabled:cursor-wait flex items-center justify-between gap-2"
                    onClick={() => addItem(s.product_id, s.description)}
                    data-testid={`stock-transfer-product-suggestion-${s.product_id}`}
                  >
                    <span>
                      <span className="font-medium text-[#344054]">{s.product_id}</span>
                      {s.description && <span className="text-[#667085]"> - {s.description}</span>}
                    </span>
                    {addingProductId === s.product_id && (
                      <span className="flex items-center gap-1 text-[#0E7C86] shrink-0" data-testid={`stock-transfer-product-suggestion-adding-${s.product_id}`}>
                        <CircleNotch size={12} className="animate-spin" /> Adding...
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Line item table */}
        {items.length > 0 && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto" data-testid="stock-transfer-line-items-card">
            <div className="px-3 py-2 border-b border-[#D0D5DD] bg-[#F9FAFB]">
              <h3 className="font-heading text-xs font-bold text-[#1D2939] uppercase tracking-wide">2-4. Inventory Check / Source Warehouse / Ship-from Site</h3>
            </div>
            <table className="w-full text-[11px] border-collapse min-w-[900px]" data-testid="stock-transfer-line-items-table">
              <thead>
                <tr>
                  {["Product", "Source Warehouse", "Ship-from Site", "Available Qty", "Requested Qty", "Status", ""].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.key} data-testid={`stock-transfer-line-${i.product_id}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-[#344054]">{i.product_id}</span>
                        <button
                          type="button"
                          title="Refresh live stock for this item from SAP (all warehouses)"
                          onClick={() => refreshItemStock(i.key, i.product_id)}
                          disabled={i.refreshing}
                          data-testid={`stock-transfer-refresh-item-${i.product_id}`}
                          className="text-[#0E7C86] hover:text-[#095b62] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {i.refreshing ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} />}
                        </button>
                      </div>
                      <div className="text-[#667085]">{i.description || "—"}</div>
                      <div className="text-[10px] mt-0.5" data-testid={`stock-transfer-hsn-${i.product_id}`}>
                        {i.hsn_code
                          ? <span className="text-[#0E7C86] font-medium">HSN: {i.hsn_code}</span>
                          : <span className="text-[#98A2B3]">HSN: Not maintained in SAP</span>}
                      </div>
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 min-w-[220px]">
                      {(() => {
                        // Sep 25 2026 fix (real user report - P2W never showed
                        // in the dropdown even though SAP had real stock
                        // there): `shipFromSiteId` used to be derived from
                        // ANY item (including this same one), so once THIS
                        // item picked a warehouse, that pick became the
                        // "lock" that then filtered its OWN dropdown down to
                        // only that same site - every other site's stock
                        // (e.g. P2W) disappeared from the list entirely, with
                        // no way to reconsider. Lock site must only come from
                        // OTHER items, never from the item's own current pick.
                        const lockSiteId = items.find((o) => o.key !== i.key && o.ship_from_site_id)?.ship_from_site_id || "";
                        return (
                        <>
                      <Select value={i.source_warehouse_id} onValueChange={(v) => chooseWarehouse(i.key, v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`stock-transfer-warehouse-select-${i.product_id}`}>
                          <SelectValue placeholder={
                            i.locations.filter((l) => l.is_usable).length === 0
                              ? "No usable stock found"
                              : (lockSiteId && !i.locations.some((l) => l.site_id === lockSiteId) ? `No stock at ${lockSiteId}` : "Choose warehouse")
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {i.locations
                            .filter((l) => !lockSiteId || l.site_id === lockSiteId || l.warehouse_id === i.source_warehouse_id)
                            // Aug 27 2026 fix: with include_non_usable=true, the
                            // SAME warehouse_id can appear twice (a usable row +
                            // a non-usable Inspection/Restricted row) - dedupe by
                            // warehouse_id (backend already sorts usable-first)
                            // to avoid React's duplicate-key warning/option glitch.
                            .filter((l, idx, arr) => arr.findIndex((x) => x.warehouse_id === l.warehouse_id) === idx)
                            .map((l) => (
                              <SelectItem
                                key={l.warehouse_id}
                                value={l.warehouse_id}
                                disabled={l.is_usable === false}
                                className={l.is_usable === false ? "text-[#98A2B3]" : undefined}
                                data-testid={l.is_usable === false ? `stock-transfer-warehouse-nonusable-${i.product_id}-${l.warehouse_id}` : undefined}
                              >
                                {l.site_id} - {l.warehouse_name || l.warehouse_id} ({formatQty(l.qty)})
                                {l.is_usable === false ? ` - ${l.stock_status || "Restricted"}, not available` : ""}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {i.is_consumable && (
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-[#B54708]" data-testid={`stock-transfer-consumable-hint-${i.product_id}`}>
                          <Shield size={11} weight="fill" /> Consumable item - HOLD warehouse only
                        </p>
                      )}
                      {i.suggestion?.suggested && i.suggestion.suggested.warehouse_id !== i.source_warehouse_id
                        && (!lockSiteId || i.suggestion.suggested.site_id === lockSiteId) && (
                        <button
                          type="button"
                          onClick={() => applySuggestion(i.key)}
                          className="mt-1 flex items-center gap-1 text-[11px] text-[#0E7C86] hover:underline"
                          data-testid={`stock-transfer-suggestion-${i.product_id}`}
                        >
                          <Sparkle size={11} /> Suggested: {i.suggestion.suggested.site_id} / {i.suggestion.suggested.warehouse_name || i.suggestion.suggested.warehouse_id}
                        </button>
                      )}
                      {i.suggestion?.suggested && lockSiteId && i.suggestion.suggested.site_id !== lockSiteId
                        && !i.locations.some((l) => l.site_id === lockSiteId && l.is_usable) && (
                        <p className="mt-1 text-[11px] text-[#98A2B3]">No stock at {lockSiteId} for this item - it would need a separate order.</p>
                      )}
                      {i.suggestion && !i.suggestion.suggested && (
                        <p className="mt-1 text-[11px] text-[#98A2B3]">{i.suggestion.reason}</p>
                      )}
                        </>
                        );
                      })()}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#667085]">{i.ship_from_site_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(i.available_qty)} {i.unit_of_measure}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <Input
                        type="number"
                        value={i.requested_qty}
                        onChange={(e) => setRequestedQty(i.key, e.target.value)}
                        className="h-8 text-xs w-28"
                        data-testid={`stock-transfer-qty-input-${i.product_id}`}
                      />
                      {i.error && <p className="text-[11px] text-[#B42318] font-bold mt-1" data-testid={`stock-transfer-qty-error-${i.product_id}`}>{i.error}</p>}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {i.locations.filter((l) => l.is_usable).length === 0 ? (
                        <span className="inline-flex items-center gap-1 text-[#B42318] font-bold"><WarningCircle size={12} /> No Stock</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#027A48] font-bold"><CheckCircle size={12} /> Available</span>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <button type="button" onClick={() => removeItem(i.key)} className="text-[#B42318] hover:text-[#912018]" data-testid={`stock-transfer-remove-${i.product_id}`}>
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Header fields: Ship-to Site / Location / Priority / Date */}
        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3">
          <h3 className="font-heading text-xs font-bold text-[#1D2939] uppercase tracking-wide">5-9. Ship-to Site, Location, Delivery Priority &amp; Date</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#344054]">Ship-from Site</Label>
              <Input value={shipFromSiteId || ""} readOnly disabled placeholder="Pick a source warehouse above" data-testid="stock-transfer-ship-from-site" />
              <p className="text-[11px] text-[#98A2B3] mt-0.5">Auto-determined from Source Warehouse.</p>
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Ship-to Site*</Label>
              <Select value={shipToSiteId} onValueChange={setShipToSiteId} disabled={!shipFromSiteId}>
                <SelectTrigger data-testid="stock-transfer-ship-to-site-select"><SelectValue placeholder="Choose Ship-to Site" /></SelectTrigger>
                <SelectContent>
                  {shipToSiteOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Ship-to Location*</Label>
              <Select value={shipToLocationId} onValueChange={setShipToLocationId} disabled={!shipToSiteId}>
                <SelectTrigger data-testid="stock-transfer-ship-to-location-select"><SelectValue placeholder="Choose Location" /></SelectTrigger>
                <SelectContent>
                  {shipToLocationOptions.map((w) => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name || w.warehouse_id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Delivery Priority</Label>
              <Input value="Immediate" readOnly disabled data-testid="stock-transfer-delivery-priority" />
            </div>
          </div>
          <div className="max-w-xs">
            <Label className="text-xs font-bold text-[#344054]">Requested Delivery Date*</Label>
            <Input
              type="date"
              min={todayISO()}
              value={requestedDeliveryDate}
              onChange={(e) => {
                // Aug 27 2026, user's explicit ask: Date Of Supply follows
                // whatever is entered here, so it doesn't have to be
                // re-typed - still independently editable afterward below.
                setRequestedDeliveryDate(e.target.value);
                setDateOfSupply(e.target.value);
              }}
              data-testid="stock-transfer-delivery-date-input"
            />
          </div>
        </div>

        {/* GST / E-way bill compliance fields - mandatory again per
            user's explicit follow-up ask (Sep 18 2026): NOT written to
            SAP (confirmed hard-locked against API writes), kept on this
            app's own record and pushed to the legacy ERP portal for the
            4 fields that actually have a column there. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs font-bold text-[#344054]">Transportation Mode<span className="text-[#D92D20]">*</span></Label>
            <Select value={transportationMode} onValueChange={setTransportationMode}>
              <SelectTrigger data-testid="stock-transfer-transportation-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["By Road", "By Rail", "By Air", "By Self"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Vehicle No.<span className="text-[#D92D20]">*</span></Label>
            <Input value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value.toUpperCase())} placeholder="e.g. MH12AB1234" data-testid="stock-transfer-vehicle-no-input" />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Place Of Supply<span className="text-[#D92D20]">*</span></Label>
            <Input value={placeOfSupply} onChange={(e) => setPlaceOfSupply(e.target.value)} placeholder="e.g. Maharashtra" data-testid="stock-transfer-place-of-supply-input" />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">G.R No.<span className="text-[#D92D20]">*</span></Label>
            <Input value={grNo} onChange={(e) => setGrNo(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="e.g. 12345" data-testid="stock-transfer-gr-no-input" />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Date Of Supply<span className="text-[#D92D20]">*</span></Label>
            <Input type="date" value={dateOfSupply} onChange={(e) => setDateOfSupply(e.target.value)} data-testid="stock-transfer-date-of-supply-input" />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Freight Forwarder<span className="text-[#D92D20]">*</span></Label>
            <Input value={freightForwarder} onChange={(e) => setFreightForwarder(e.target.value)} placeholder="e.g. ABC Logistics" data-testid="stock-transfer-freight-forwarder-input" />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Remark</Label>
            <Input value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Optional note" data-testid="stock-transfer-remark-input" />
          </div>
        </div>


        {formError && (
          <div className="bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-3 text-sm text-[#912018] flex items-center gap-2" data-testid="stock-transfer-form-error">
            <WarningCircle size={16} /> {formError}
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#1D2939] text-white text-xs font-bold shrink-0">1</span>
            <Button
              onClick={runValidateAndStage}
              disabled={validating}
              variant={sapValidated ? "outline" : "default"}
              className="w-full sm:w-auto"
              data-testid="stock-transfer-validate-button"
            >
              {validating && <CircleNotch size={14} className="animate-spin mr-1.5" />}
              {validating ? "Validating & Staging..." : sapValidated ? "Validated + Staged ✓" : "Validate + Stage Stock"}
            </Button>
          </div>
          {!sapValidated && (
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-bold shrink-0 bg-[#D0D5DD]">2</span>
              <Button
                disabled
                className="w-full sm:w-auto"
                data-testid="stock-transfer-create-button"
                title="Run Validate + Stage Stock first"
              >
                Review &amp; Create Stock Transfer Order <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          )}
        </div>

        {/* Sep 22 2026, 4-button STO redesign, the Confirm/Cancel gate
            between Button 1 and Button 2 (STO_4BUTTON_REDESIGN.md
            section 2) - stock is already physically staged in HOLD at
            this point (the only side effect before Confirm); NO SAP
            order exists yet. */}
        {/* Sep 25 2026 fix (real user report, screenshot) - this used to
            stay expanded (with its own Cancel/Confirm buttons) even after
            "Confirm - Continue to Create STO" opened the "Confirm Stock
            Transfer Order" panel below, showing two redundant confirm/
            cancel actions stacked on screen at once. Now collapses away
            once the confirm dialog is open (reappears if the user clicks
            "Cancel, go back" there, since showConfirmDialog flips back). */}
        {sapValidated && stagingId && !showConfirmDialog && (
          <div className="bg-white border border-[#0E7C86] rounded-sm p-3 space-y-3" data-testid="stock-transfer-staging-summary">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-heading text-xs font-bold text-[#1D2939] uppercase tracking-wide">Stock Staged in HOLD - Review, then Confirm or Cancel</h3>
              {stagedItems.length > 0 && (
                <button
                  type="button"
                  className="text-[11px] text-[#0E7C86] font-medium shrink-0 hover:underline"
                  onClick={() => setShowStagingDetails((v) => !v)}
                  data-testid="stock-transfer-staging-toggle-details"
                >
                  {showStagingDetails ? "Hide item details" : "Show item details"}
                </button>
              )}
            </div>
            {stagedItems.length > 0 ? (
              <>
                {showStagingDetails && (
                  <div className="border border-[#EAECF0] rounded-sm overflow-auto">
                    <table className="w-full text-[11px] border-collapse" data-testid="stock-transfer-staging-items-table">
                      <thead>
                        <tr>
                          {["Product", "Source", "Staged To", "Requested Qty", "Moved Now"].map((h) => (
                            <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-[11px] font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {stagedItems.map((it, idx) => (
                          <tr key={`${it.product_id}-${it.source_warehouse_id}-${it.target_warehouse_id}`} data-testid={`stock-transfer-staging-item-${idx}`}>
                            <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium">{it.product_id}</td>
                            <td className="border border-[#D0D5DD] px-2 py-1.5">{it.source_warehouse_id}</td>
                            <td className="border border-[#D0D5DD] px-2 py-1.5">{it.target_warehouse_id}</td>
                            <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(it.requested_qty)} {it.unit_of_measure}</td>
                            <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums font-medium text-[#027A48]">{formatQty(it.move_qty)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[11px] text-[#667085]" data-testid="stock-transfer-staging-hold-explainer">
                  Every line gets a fresh Goods Movement into HOLD for its full requested quantity, every time.
                  {stagingGacId && <> SAP Goods Movement ID: <span className="font-medium text-[#344054]" data-testid="stock-transfer-staging-gac-id">{stagingGacId}</span>.</>}
                </p>
              </>
            ) : (
              <p className="text-sm text-[#667085]" data-testid="stock-transfer-staging-no-movement">
                {/* Sep 25 2026 fix (real user report - stock was NOT in HOLD,
                    message was wrong): item_summaries only comes back empty
                    for sites with no HOLD warehouse at all (SITES_WITHOUT_
                    HOLD_WAREHOUSE) - every other path always returns at
                    least one row (even move_qty=0 ones), so the old
                    "already sitting in HOLD" alternative could never
                    actually be true here and was misleading. */}
                {shipFromSiteId || "This site"} has no HOLD staging warehouse in SAP - your stock stays exactly where it already is; no relocation was needed or attempted.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCancelStaging} disabled={cancelingStaging || confirmingStaging} data-testid="stock-transfer-staging-cancel-button" title={stagedItems.length > 0 ? "Abandons this order - the staged stock stays in HOLD as-is, no SAP order was ever created." : "Abandons this order - no stock was moved, nothing to reverse."}>
                {cancelingStaging && <CircleNotch size={14} className="animate-spin mr-1.5" />}
                {cancelingStaging ? "Cancelling..." : "Cancel"}
              </Button>
              <Button type="button" onClick={handleConfirmStaging} disabled={confirmingStaging || cancelingStaging} data-testid="stock-transfer-staging-confirm-button">
                {confirmingStaging && <CircleNotch size={14} className="animate-spin mr-1.5" />}
                {confirmingStaging ? "Confirming..." : "Confirm - Continue to Create STO"} <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        <Dialog open={showValidationErrorBox} onOpenChange={setShowValidationErrorBox}>
          <DialogContent className="max-w-lg" data-testid="stock-transfer-validation-error-box">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#912018]"><WarningCircle size={18} /> Validation found {validationIssues.length} issue{validationIssues.length === 1 ? "" : "s"}</DialogTitle>
              <DialogDescription>Fix these before creating the Stock Transfer Order - SAP would reject it otherwise.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {validationIssues.map((issue, idx) => (
                <div key={`${issue.field}-${issue.message}`} className="bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-2.5 text-sm text-[#912018] flex items-start justify-between gap-2" data-testid={`stock-transfer-validation-issue-${idx}`}>
                  <span>{issue.message}</span>
                  {["stock", "valuation", "activation"].includes(issue.field) && (
                    <Button
                      type="button" size="sm" variant="outline" className="shrink-0 h-7 text-xs"
                      disabled={retryingCheck === issue.field}
                      onClick={() => retryCheckCategory(issue.field)}
                      data-testid={`stock-transfer-retry-check-${issue.field}`}
                    >
                      {retryingCheck === issue.field ? "Retrying..." : `Retry ${CHECK_LABELS[issue.field] || issue.field}`}
                    </Button>
                  )}
                  {issue.field === "staging" && (
                    <Button
                      type="button" size="sm" variant="outline" className="shrink-0 h-7 text-xs"
                      disabled={validating}
                      onClick={() => { setShowValidationErrorBox(false); runValidateAndStage(); }}
                      data-testid="stock-transfer-retry-staging-button"
                    >
                      {validating ? "Retrying..." : "Retry Staging"}
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button onClick={() => setShowValidationErrorBox(false)} className="w-full" data-testid="stock-transfer-validation-error-ok-button">OK</Button>
          </DialogContent>
        </Dialog>

        {/* Sep 22 2026, 4-button STO redesign - user's explicit ask:
            no modal for Steps 2-4, just a persistent inline panel right
            below Button 1/2 so the whole 1->2->3->4 journey stays on
            the page - no popup. The separate "click a row to view
            details" modal further below is a different, kept-as-is use
            case (browsing any PAST order's summary later). */}
        {showConfirmDialog && (
          <div className="bg-white border border-[#0E7C86] rounded-sm p-4 space-y-3" data-testid="stock-transfer-confirm-dialog">
            <div>
              <h3 className="font-heading text-sm font-bold text-[#1D2939]">Confirm Stock Transfer Order</h3>
              <p className="text-xs text-[#667085] mt-0.5">This will be written LIVE to SAP immediately - it cannot be undone from this app. Please review carefully.</p>
            </div>

            {sapSubmitPhase ? (
              <div className="space-y-3" data-testid="stock-transfer-sap-submit-status">
                <ol className="space-y-2.5">
                  {STO_STEPS.map((step) => {
                    const state = step.key === "validate" ? (stepStatuses.validate || "pending") : (stepStatuses[step.key] || "pending");
                    return (
                      <li key={step.key} className="flex items-center gap-2.5 text-sm" data-testid={`stock-transfer-step-${step.key}`} data-step-state={state}>
                        {state === "done" && <CheckCircle size={18} weight="fill" className="text-[#12B76A] shrink-0" />}
                        {state === "active" && <CircleNotch size={18} className="text-[#7A5AF8] shrink-0 animate-spin" />}
                        {state === "failed" && <WarningCircle size={18} weight="fill" className="text-[#F04438] shrink-0" />}
                        {state === "pending" && <span className="w-[18px] h-[18px] rounded-full border-2 border-[#D0D5DD] shrink-0" />}
                        <span className={state === "pending" ? "text-[#98A2B3]" : state === "failed" ? "text-[#912018] font-medium" : "text-[#344054] font-medium"}>{step.label}</span>
                      </li>
                    );
                  })}
                </ol>
                {(sapSubmitPhase === "failed" || (sapSubmitPhase === "done" && !createdOrderLive)) && (
                  <div
                    className={`rounded-sm p-3 text-sm flex items-start gap-2 ${
                      sapSubmitPhase === "done" ? "bg-[#ECFDF3] border border-[#ABEFC6] text-[#027A48]"
                      : "bg-[#FEF3F2] border border-[#FDA29B] text-[#912018]"
                    }`}
                    data-testid="stock-transfer-sap-submit-result"
                  >
                    {sapSubmitPhase === "done" ? <CheckCircle size={16} className="mt-0.5 shrink-0" /> : <WarningCircle size={16} className="mt-0.5 shrink-0" />}
                    <p>{sapSubmitMessage}</p>
                  </div>
                )}
                {sapSubmitPhase === "done" && createdOrderLive && (
                  <OrderDetailBody
                    order={createdOrderLive}
                    retryingStoId={retryingStoId} onRetryOrder={handleRetryOrder}
                    retryingErpStoId={retryingErpStoId} onRetryErpSync={handleRetryErpSync} onManualErpLink={handleManualErpLink}
                    verifyingErpStoId={verifyingErpStoId} onVerifyErpSync={handleVerifyErpSync}
                    retryingPostingStoId={retryingPostingStoId} onRetryPosting={handleRetryPosting}
                    completingGiStoId={completingGiStoId} onCompleteManualGi={handleCompleteManualGi}
                    pausingAutoRetryStoId={pausingAutoRetryStoId} onPauseAutoRetry={handlePauseAutoRetry}
                    stoppingGiStoId={stoppingGiStoId} onForceStopGi={handleForceStopGi} isAdmin={isAdmin}
                    notifications={notifications} activateResults={activateResults} activatingId={activatingId}
                    confirmActivateFor={confirmActivateFor} setConfirmActivateFor={setConfirmActivateFor} onActivate={handleActivate}
                  />
                )}
              </div>

            ) : (
              <>
                {stagingId && (
                  <div className="bg-[#ECFDF3] border border-[#ABEFC6] rounded-sm p-2.5 text-xs text-[#027A48] flex items-center gap-2" data-testid="stock-transfer-confirm-staged-banner">
                    <CheckCircle size={14} className="shrink-0" /> Stock for these lines was already staged into HOLD by "Validate + Stage Stock" - creating this order now will NOT move it again.
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <div><Label className="text-xs font-bold text-[#344054]">Ship-from Site</Label><p data-testid="stock-transfer-confirm-ship-from">{shipFromSiteId || "—"}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Ship-to Site</Label><p data-testid="stock-transfer-confirm-ship-to">{shipToSiteId}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Ship-to Location</Label><p>{shipToLocationOptions.find((w) => w.warehouse_id === shipToLocationId)?.warehouse_name || shipToLocationId}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Delivery Priority</Label><p>Immediate</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Requested Delivery Date</Label><p>{requestedDeliveryDate}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Transportation Mode</Label><p>{transportationMode}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Vehicle No.</Label><p>{vehicleNo}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Place Of Supply</Label><p>{placeOfSupply}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">G.R No.</Label><p>{grNo}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Date Of Supply</Label><p>{dateOfSupply}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Freight Forwarder</Label><p data-testid="stock-transfer-confirm-freight-forwarder">{freightForwarder}</p></div>
                  <div><Label className="text-xs font-bold text-[#344054]">Remark</Label><p data-testid="stock-transfer-confirm-remark">{remark || "—"}</p></div>
                </div>
                <div className="border border-[#EAECF0] rounded-sm overflow-auto">
                  <table className="w-full text-[11px] border-collapse" data-testid="stock-transfer-confirm-items-table">
                    <thead>
                      <tr>
                        {["Product", "Source Warehouse", "Requested Qty"].map((h) => (
                          <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-[11px] font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((i) => (
                        <tr key={i.key}>
                          <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium">{i.product_id}</td>
                          <td className="border border-[#D0D5DD] px-2 py-1.5">{i.source_warehouse_id}</td>
                          <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(Number(i.requested_qty))} {i.unit_of_measure}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="outline" onClick={() => setShowConfirmDialog(false)} data-testid="stock-transfer-confirm-cancel-button">Cancel, go back</Button>
                  <Button type="button" onClick={confirmAndSubmit} disabled={submitting} data-testid="stock-transfer-confirm-submit-button">
                    {submitting ? "Submitting..." : "Confirm & Submit to SAP"}
                  </Button>
                </div>
              </>
            )}

            {sapSubmitPhase && sapSubmitPhase !== "submitting" && (
              <div className="flex justify-end pt-1">
                <Button type="button" onClick={() => { giPollStopRef.current = true; setShowConfirmDialog(false); setSapSubmitPhase(null); setSapSubmitMessage(null); setStepStatuses({}); setCreatedOrderLive(null); }} data-testid="stock-transfer-confirm-close-button">Close</Button>
              </div>
            )}
          </div>
        )}

        {/* Sep 7 2026, user's explicit ask: hide this aggregated "Action
            Needed" panel from the main page - the exact same Activate
            action already surfaces inline on each affected order's own
            detail view (OrderDetailBody above, via `notifications` prop),
            so this was redundant clutter. Notifications data/activation
            logic is kept (still feeds that per-order view) - only this
            panel's rendering is removed. */}

        {/* Recent orders */}
        <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto" data-testid="stock-transfer-recent-card">
          <div className="px-3 py-2 border-b border-[#D0D5DD] bg-[#F9FAFB]">
            <h3 className="font-heading text-xs font-bold text-[#1D2939] uppercase tracking-wide">
              Recent Stock Transfer Orders ({hasActiveRecentFilters ? `${filteredRecentOrders.length} of ${recentOrders.length}` : recentOrders.length})
            </h3>
          </div>

          <div className="px-3 py-2.5 border-b border-[#D0D5DD] bg-white flex flex-wrap items-end gap-2" data-testid="stock-transfer-recent-filter-row">
            <div className="w-[120px]">
              <Label className="text-[10px] font-bold text-[#667085] uppercase">STO ID</Label>
              <Input value={filterStoId} onChange={(e) => setFilterStoId(e.target.value)} placeholder="e.g. 165" className="h-8 text-xs" data-testid="stock-transfer-filter-sto-id" />
            </div>
            <div className="w-[140px]">
              <Label className="text-[10px] font-bold text-[#667085] uppercase">Created Date</Label>
              <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="h-8 text-xs" data-testid="stock-transfer-filter-date" />
            </div>
            <div className="w-[150px]">
              <Label className="text-[10px] font-bold text-[#667085] uppercase">Created By</Label>
              <Select value={filterCreatedBy} onValueChange={setFilterCreatedBy}>
                <SelectTrigger className="h-8 text-xs" data-testid="stock-transfer-filter-created-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {distinctCreatedBy.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[130px]">
              <Label className="text-[10px] font-bold text-[#667085] uppercase">Ship-from</Label>
              <Select value={filterShipFrom} onValueChange={setFilterShipFrom}>
                <SelectTrigger className="h-8 text-xs" data-testid="stock-transfer-filter-ship-from">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {distinctShipFrom.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[130px]">
              <Label className="text-[10px] font-bold text-[#667085] uppercase">Ship-to</Label>
              <Select value={filterShipTo} onValueChange={setFilterShipTo}>
                <SelectTrigger className="h-8 text-xs" data-testid="stock-transfer-filter-ship-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {distinctShipTo.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[170px]">
              <Label className="text-[10px] font-bold text-[#667085] uppercase">Item</Label>
              <Input value={filterItem} onChange={(e) => setFilterItem(e.target.value)} placeholder="Product ID or name" className="h-8 text-xs" data-testid="stock-transfer-filter-item" />
            </div>
            {hasActiveRecentFilters && (
              <Button variant="ghost" size="sm" onClick={clearRecentFilters} className="h-8 text-xs text-[#B42318]" data-testid="stock-transfer-filter-clear">
                Clear Filters
              </Button>
            )}
          </div>

          {loadingRecent ? (
            <p className="p-4 text-xs text-[#667085]">Loading...</p>
          ) : recentOrders.length === 0 ? (
            <p className="p-4 text-xs text-[#98A2B3]" data-testid="stock-transfer-recent-empty">No Stock Transfer Orders yet.</p>
          ) : filteredRecentOrders.length === 0 ? (
            <p className="p-4 text-xs text-[#98A2B3]" data-testid="stock-transfer-recent-filtered-empty">No orders match these filters.</p>
          ) : (
            <table className="w-full text-[11px] border-collapse table-fixed" data-testid="stock-transfer-recent-table">
              <colgroup>
                <col className="w-[7%]" /><col className="w-[7%]" /><col className="w-[7%]" /><col className="w-[5%]" /><col className="w-[5%]" />
                <col className="w-[7%]" /><col className="w-[4%]" /><col className="w-[6%]" /><col className="w-[8%]" /><col className="w-[8%]" />
                <col className="w-[8%]" /><col className="w-[7%]" /><col className="w-[6%]" /><col className="w-[7%]" /><col className="w-[8%]" />
              </colgroup>
              <thead>
                <tr>
                  {["STO ID", "Created", "By", "Ship-from", "Ship-to", "Location", "Items", "Delivery Date", "SAP Order ID", "Status", "Warehouse Move", "Goods Issue", "SAP User", "GST Push", "ERP Portal"].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-[11px] font-bold text-[#344054] font-heading uppercase break-words">{h}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {draftInfo?.draft && (
                  <tr className="bg-[#FFFAEB]" data-testid="stock-transfer-recent-draft-row">
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium text-[#93370D]">Draft</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{Math.max(1, Math.round(draftInfo.age_seconds / 60))} min ago</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{user?.name || user?.email || "You"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{draftInfo.draft.items?.[0]?.source_warehouse_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{draftInfo.draft.ship_to_site_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">—</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{(draftInfo.draft.items || []).map((i) => i.product_id).join(", ") || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{draftInfo.draft.requested_delivery_date || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">—</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <StatusText tone="warning">Draft - Not Yet Submitted</StatusText>
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">—</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">—</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">—</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">—</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <div className="flex gap-1.5">
                        <button type="button" className="text-[11px] text-[#0E7C86] font-medium hover:underline" onClick={restoreDraftIntoForm} data-testid="stock-transfer-recent-draft-restore-button">Restore</button>
                        <button type="button" className="text-[11px] text-[#98A2B3] font-medium hover:underline" onClick={discardDraft} data-testid="stock-transfer-recent-draft-discard-button">Discard</button>
                      </div>
                    </td>
                  </tr>
                )}
                {filteredRecentOrders.map((o) => {
                  const badge = o.error_message
                    ? { label: "Issue Found", tone: "danger" }
                    : o.status === "created_in_sap"
                    ? { label: "Created In SAP", tone: "success" }
                    : { label: "Pending SAP", tone: "warning" };
                  const giBadge = o.gi_status === "posted"
                    ? { label: "GI Posted", tone: "success" }
                    : o.gi_status === "failed"
                    ? { label: "GI Failed", tone: "danger" }
                    : o.gi_status === "not_found_timeout"
                    ? { label: "GI Pending (20min+)", tone: "warning" }
                    : o.gi_status === "insufficient_stock"
                    ? { label: "Insufficient Stock", tone: "warning" }
                    : o.gi_status === "awaiting_manual_gi"
                    ? { label: "Awaiting Manual GI", tone: "info" }
                    : o.gi_status === "awaiting_delivery"
                    ? { label: "Awaiting Delivery", tone: "warning" }
                    : null;
                  const erpBadge = o.erp_portal_status === "synced"
                    ? { label: "Synced", tone: "success" }
                    : o.erp_portal_status === "failed"
                    ? { label: "Sync Failed", tone: "danger" }
                    : o.erp_portal_status === "paused"
                    ? { label: "Sync Paused", tone: "neutral" }
                    : o.erp_portal_status === "retrying"
                    ? { label: "Retrying...", tone: "warning" }
                    : o.status === "created_in_sap"
                    ? { label: "Pending", tone: "warning" }
                    : null;
                  const relocatedLines = (o.items || []).filter((it) => it.p8_relocation).map((it) => ({ product_id: it.product_id, gac_id: it.p8_relocation.gac_id }));
                  const relocationTo = movDisplayName((o.items || []).find((it) => it.p8_relocation)?.p8_relocation?.to);
                  return (
                    <tr
                      key={o.sto_id}
                      onClick={() => setSelectedOrder(o)}
                      className="cursor-pointer hover:bg-[#F9FAFB]"
                      data-testid={`stock-transfer-recent-row-${o.sto_id}`}
                    >
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium text-[#0E7C86] underline whitespace-nowrap">{o.sto_id}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{new Date(o.created_at).toLocaleString("en-IN")}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 truncate" title={o.created_by}>{o.created_by}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{o.ship_from_site_id}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{o.ship_to_site_id}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 truncate" title={o.ship_to_location_name || o.ship_to_location_id}>{o.ship_to_location_name || o.ship_to_location_id}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{o.items?.length || 0}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{o.requested_delivery_date}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono break-all" data-testid={`stock-transfer-recent-sap-id-${o.sto_id}`}>
                        {formatSapId(o.sap_order_id) || "—"}
                        {o.outbound_delivery_ids?.length > 0 ? (
                          <div className="mt-0.5 text-[10px] font-semibold" data-testid={`stock-transfer-recent-outbound-no-${o.sto_id}`}>
                            <span className="text-[#98A2B3] font-normal">Outbound: </span>
                            <span className="text-[#15803D]">{o.outbound_delivery_ids.join(", ")}</span>
                          </div>
                        ) : o.gi_status === "awaiting_manual_gi" && o.gi_delivery_request_id && (
                          <div className="mt-0.5 text-[10px] font-semibold" data-testid={`stock-transfer-recent-delivery-request-${o.sto_id}`}>
                            <span className="text-[#98A2B3] font-normal">Delivery Req: </span>
                            <span className="text-[#175CD3]">{o.gi_delivery_request_id}</span>
                          </div>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">
                        <StatusText tone={badge.tone}>{badge.label}</StatusText>
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`stock-transfer-recent-relocation-${o.sto_id}`}>
                        {relocatedLines.length > 0 ? (
                          <RelocationPopover lines={relocatedLines} to={relocationTo} stoId={o.sto_id}>
                            <span className="text-[#027A48] font-medium">Moved to {relocationTo}</span>
                          </RelocationPopover>
                        ) : (
                          <span className="text-[#98A2B3]">—</span>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">
                        {giBadge ? <StatusText tone={giBadge.tone}>{giBadge.label}</StatusText> : <span className="text-[#98A2B3]">—</span>}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono truncate" title={o.gi_playwright_user || ""} data-testid={`stock-transfer-recent-sap-user-${o.sto_id}`}>{o.gi_playwright_user || "—"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">
                        {o.gst_note_pushed ? (
                          <StatusText tone="success" testId={`stock-transfer-recent-gst-status-${o.sto_id}`}>GST Recorded</StatusText>
                        ) : (
                          <span className="text-[#98A2B3]" data-testid={`stock-transfer-recent-gst-status-${o.sto_id}`}>—</span>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`stock-transfer-recent-erp-status-${o.sto_id}`}>
                        {erpBadge ? <StatusText tone={erpBadge.tone}>{erpBadge.label}</StatusText> : <span className="text-[#98A2B3]">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Detail modal (Aug 2026, user's explicit ask) - clicking any row
          above opens the full breakdown; any SAP-write failure's
          error_message is surfaced here verbatim, human-readable, never a
          raw stack trace. */}
      <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="stock-transfer-detail-modal">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedOrder.sto_id}</DialogTitle>
                <DialogDescription>Stock Transfer Order details and live SAP status</DialogDescription>
              </DialogHeader>
              <OrderDetailBody
                order={selectedOrder}
                retryingStoId={retryingStoId} onRetryOrder={handleRetryOrder}
                retryingErpStoId={retryingErpStoId} onRetryErpSync={handleRetryErpSync} onManualErpLink={handleManualErpLink}
                verifyingErpStoId={verifyingErpStoId} onVerifyErpSync={handleVerifyErpSync}
                retryingPostingStoId={retryingPostingStoId} onRetryPosting={handleRetryPosting}
                completingGiStoId={completingGiStoId} onCompleteManualGi={handleCompleteManualGi}
                pausingAutoRetryStoId={pausingAutoRetryStoId} onPauseAutoRetry={handlePauseAutoRetry}
                stoppingGiStoId={stoppingGiStoId} onForceStopGi={handleForceStopGi} isAdmin={isAdmin}
                notifications={notifications} activateResults={activateResults} activatingId={activatingId}
                confirmActivateFor={confirmActivateFor} setConfirmActivateFor={setConfirmActivateFor} onActivate={handleActivate}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
